﻿import { SmartComply } from "../client/Smartcomply";
import { SDKInitConfig, SDKChannel, ChannelsByCategory, VerifyIdentityResponse } from "../types/onboarding";
import { LivenessSubmitResponse, ChallengeAction } from "../types/liveness";
import { ThemeColors, getTheme, TYPE, RADIUS } from "./theme";
import { DocumentCapture, DocumentCaptureCancelledError, DocumentCaptureResult } from "./DocumentCapture";
import { countryCodeFor, countryLabel, filterCountries, isUploadFieldType } from "../utils/channels";
import { Environment } from "../client/Config";
import { collectDeviceMetadata } from "../utils/device";
import { requiresBackSide, BackSideMode } from "../utils/documentRules";
import { DEFAULT_CHALLENGE_ACTIONS, MAX_DOCUMENT_BYTES } from "../utils/constants";
import { icon, IconName } from "./icons";
import { renderSpinner, renderInlineSpinner } from "./spinner";
import { SDKError } from "../errors/SDKError";

// A dropped camera/network/upload error is not the user's fault and must not
// cost them a retry attempt — only a genuine verification outcome should.
// Classified by message rather than a typed error class since the errors
// originate from several independent layers (CameraManager, VideoRecorder,
// LivenessUploader) that don't share a common error type today.
const _INFRASTRUCTURE_ERROR_PATTERNS = [
  "camera connection was lost",
  "network error",
  "failed after all retries",
  "camera access is not supported",
  "camera access failed",
  "recording failed unexpectedly",
  // FaceDetectorEngine.init() failures (FaceDetector.ts) — a slow/blocked
  // CDN or model download is a connectivity issue, not a failed face scan,
  // and must not burn one of the user's limited liveness retry attempts.
  "face detection setup timed out",
  "failed to download face model",
];

function _isInfrastructureError(err: any): boolean {
  const msg = String(err?.message || "").toLowerCase();
  return _INFRASTRUCTURE_ERROR_PATTERNS.some((p) => msg.includes(p));
}

// Keep this plain-language and reassuring for infrastructure hiccups — the
// user did nothing wrong, so the copy should say so rather than sound like
// a rejection.
function _userFacingLivenessError(err: any): string {
  if (_isInfrastructureError(err)) {
    return "We lost connection during your scan — this wasn't anything you did. Please check your camera and internet connection, then try again.";
  }
  return err?.message || "We couldn't verify your face. Please try again.";
}

// ─────────────────────────────────────────────────────────────────────
// Public Types
// ─────────────────────────────────────────────────────────────────────

export interface FlowOptions {
  apiKey: string;
  clientId: string;
  environment?: Environment;
  timeout?: number;
  /**
   * Resume a previously-failed verification instead of starting a new
   * session — the token from a client-initiated resubmit invite (the
   * "Resubmit" action on a failed session in the Adhere dashboard). Read
   * this from wherever your own resume link puts it (a query param, a
   * deep-link payload, etc.) and pass it straight through here; the SDK
   * does not parse the host page's URL itself.
   *
   * Authenticates the widget as the resumed session (instead of creating a
   * new one) and looks up which step failed, so the customer lands
   * directly on document_capture or liveness instead of the welcome
   * screen — skipping country/ID-type selection, which is pre-filled from
   * the original attempt. Falls back to the normal welcome screen if that
   * lookup doesn't resolve cleanly (an already-used/expired token, or the
   * original channel no longer exists in this config); the resume itself
   * still works correctly via the backend's own resume-match once
   * create/submit is called either way — this only affects which screen
   * is shown first.
   *
   * A data-verification/liveness-only failure has no document channel to
   * re-select, so it goes straight to the liveness step.
   */
  resumeToken?: string;
  onComplete?: (result: FlowResult) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

export interface FlowResult {
  entryId: number;
  sessionId: string | null;
  status: string;
  submittedAt: string | null;
  verificationResult?: VerifyIdentityResponse;
}

// ─────────────────────────────────────────────────────────────────────
// Step definitions
// ─────────────────────────────────────────────────────────────────────

type StepName =
  | "loading"
  | "welcome"
  | "country"
  | "id_type"
  | "id_input"
  | "confirm_identity"
  | "document_capture"
  | "document_confirm"
  | "ocr_gate"
  | "liveness"
  | "done"
  | "error";

const STEP_ORDER: StepName[] = [
  "loading",
  "welcome",
  "country",
  "id_type",
  "id_input",
  "confirm_identity",
  "document_capture",
  "ocr_gate",
  "document_confirm",
  "liveness",
  "done",
];

// From this many countries up, the country screen shows a search box: a
// setup can offer passports from every supported country (188).
const COUNTRY_SEARCH_MIN = 7;

/**
 * Whether a SPECIFIC channel is a document-scan item, vs. a plain
 * number-entry one. channels[country] is a flat list that can mix
 * data_verification channels (e.g. bare "Passport", a number-entry lookup)
 * with document_verification channels (e.g. "Document Verification -
 * Passport", a scan) whenever an SDK config's verification_type covers
 * both modes — a real, valid dual-mode setup, not a misconfiguration.
 *
 * Reported live: with both modes enabled, every channel rendered and
 * routed as a document scan, including number-entry ones — because the
 * per-channel decision fell back to the config's overall
 * globalDocFlow/verification_type the moment document_verification was
 * enabled ANYWHERE in the config, instead of checking whether THIS
 * channel actually has an upload field. A legacy string-only channel
 * (typeof idType === "string") carries no fields array to check at all,
 * so that's the only case the global flow mode is still used for.
 */
export function isChannelDocumentFlow(
  idType: string | SDKChannel,
  globalDocFlow: boolean
): boolean {
  if (typeof idType === "string") return globalDocFlow;
  return (idType.fields || []).some((f: any) => isUploadFieldType(f.type));
}

/**
 * Picks the channel list for the "Select document type" / "Select ID type"
 * screen: the list matching this screen's mode, both lists when the config
 * enables both modes, and the other list when this mode has nothing for the
 * country (so the screen is never an empty dead end).
 *
 * Reported live: the deployed widget's document-scan screen showed a bare
 * "Passport" (data_verification, number-entry) right next to "Document
 * Verification - Passport" (document_verification, scan) as if they were
 * the same kind of option — because channels[country] used to be one flat
 * list with no category split at all. Splitting the backend response
 * (SDKSecureLoaderView, adhere-backend) into {data_verification,
 * document_verification} only helps if this screen then reads the ONE
 * list matching globalDocFlow, instead of still flattening both — that's
 * what actually removes the duplicate, not just how each entry displays
 * (see isChannelDocumentFlow above, which only decides that).
 */
export function selectIdTypesForScreen(
  byCategory: ChannelsByCategory | undefined,
  globalDocFlow: boolean,
  verificationTypes: string[] = []
): (string | SDKChannel)[] {
  if (!byCategory) return [];
  const documents = byCategory.document_verification || [];
  const data = byCategory.data_verification || [];
  // A config with both modes offers both kinds; each card then routes on its
  // own channel (isChannelDocumentFlow), scan or number entry.
  if (verificationTypes.includes("document_verification") && verificationTypes.includes("data_verification")) {
    return [...documents, ...data];
  }
  const primary = globalDocFlow ? documents : data;
  // Reported live: a country with nothing in this screen's category showed
  // an empty "Select document type" screen with no way forward. Showing the
  // other category is what the widget did before the split (1.0.94).
  return primary.length > 0 ? primary : (globalDocFlow ? data : documents);
}

/**
 * Whether a showErrorStep() message is about an expired/invalid resume or
 * resubmit link (RESUME_TARGET_UNAVAILABLE / NOT_A_RESUME_TOKEN — see
 * SmartComplyFlow._resolveResumeStep), rather than a plain session timeout.
 * Extracted to a standalone function so this specific classification is
 * independently testable, same as resolveIdTypeInfo below — showErrorStep's
 * own regex-based classification block is otherwise tightly coupled to DOM
 * rendering it doesn't make sense to extract wholesale for this fix.
 */
export function isResumeLinkErrorMessage(message: string): boolean {
  return /resume link|resubmit link/i.test(message);
}

export function resolveIdTypeInfo(
  typeName: string,
  isDocumentFlow: boolean
): { icon: string; desc: string; requiresBack?: BackSideMode } {
  const u = typeName.toUpperCase();
  const advanced  = u.includes("ADVANCED");

  // Strip UI noise from display labels — users don't need "with face" or "(NIN)" suffixes
  const cleanName = typeName
    .replace(/\s*with\s+face\s*/gi, ' ')
    .replace(/\s*\(NIN\)\s*/gi, ' ')
    .replace(/\s*\(BVN\)\s*/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  // BVN — always 11 digits
  if (u.includes("BVN") || u.includes("BANK VERIFICATION")) {
    return { icon: "🏦", desc: "Enter your 11-digit Bank Verification Number (BVN)", requiresBack: false };
  }

  // NIN — always 11 digits (covers nin_with_face, NIN slip, etc.). Not every
  // NIN document in circulation has a back side — a NIN slip typically
  // doesn't, while a NIN card does — so the back photo is offered for
  // either, but optional rather than required: add it if your document
  // has one, skip it if it doesn't. The plain data-verification flow (just
  // typing the 11-digit number, no document) never captures a photo at
  // all, so requiresBack stays false there.
  if (u.includes("NIN") || u.includes("NATIONAL IDENTITY NUMBER")) {
    return {
      icon: "🆔",
      desc: isDocumentFlow
        ? "Capture or upload your NIN (slip or card) — add the back if your document has one"
        : "Enter your 11-digit National Identification Number (NIN)",
      requiresBack: isDocumentFlow ? "optional" : false,
    };
  }

  // Ghana Card / Ghana National ID
  if (u.includes("GHANA")) {
    if (isDocumentFlow) return { icon: "🪪", desc: "Capture or upload your Ghana Card", requiresBack: requiresBackSide(typeName) };
    return { icon: "🪪", desc: "Enter your Ghana Card number", requiresBack: false };
  }

  // SSNIT (Ghana social security)
  if (u.includes("SSNIT"))
    return { icon: "🏛️", desc: "Enter your SSNIT number", requiresBack: false };

  // Kenya National ID
  if (u.includes("KENYA") || (u.includes("NATIONAL") && u.includes("KENYA")))
    return { icon: "🆔", desc: "Enter your Kenya National ID number", requiresBack: false };

  // TIN
  if (u.includes("TIN") || u.includes("TAX IDENTIFICATION"))
    return { icon: "🧾", desc: "Enter your Tax Identification Number (TIN)", requiresBack: false };

  // CAC / Company registration
  if (u.includes("CAC") || u.includes("CORPORATE AFFAIRS") || u.includes("COMPANY"))
    return { icon: "🏢", desc: advanced ? "Enter RC number, company name & type" : "Enter your company registration number", requiresBack: false };

  // Voter / PVC
  if (u.includes("VOTER") || u.includes("PVC")) {
    const requiresBack = requiresBackSide(typeName);
    return { 
      icon: "🗳️", 
      desc: isDocumentFlow 
        ? `Capture or upload your voter's card${requiresBack ? " (front & back)" : ""}` 
        : "Enter your voter ID number",
      requiresBack: isDocumentFlow ? requiresBack : false
    };
  }

  // Passport
  if (u.includes("PASSPORT"))
    return { icon: "📘", desc: isDocumentFlow ? "Capture or upload your passport" : "Enter your passport number", requiresBack: false };

  // Driver's license
  if (u.includes("DRIVER") || u.includes("LICENSE") || u.includes("LICENCE")) {
    const requiresBack = requiresBackSide(typeName);
    return { 
      icon: "🚗", 
      desc: isDocumentFlow 
        ? `Capture or upload your driver's license${requiresBack ? " (front & back)" : ""}` 
        : "Enter your driver's license number",
      requiresBack: isDocumentFlow ? requiresBack : false
    };
  }

  // National ID card (generic) — by the time we reach this branch, Ghana
  // and Kenya have already matched their own, more specific branches above,
  // so what's left is Nigeria's "Document Verification - National ID"
  // channel — the document_verification-side label for the same NIN
  // document (slip or card) the NIN branch above handles for
  // data_verification. It deliberately doesn't contain "NIN" or "National
  // Identity Number" in its name, so it can't match that branch's check —
  // treated here the same way: optional, not forced, since a NIN slip has
  // no back side to capture. Caught from a real report: a NIN slip upload
  // was stuck on a mandatory "Step 2 of 2 — back side" screen with no way
  // to skip.
  if (u.includes("NATIONAL ID") || (u.includes("NATIONAL") && u.includes("CARD"))) {
    return {
      icon: "🪪",
      desc: isDocumentFlow
        ? "Capture or upload your NIN (slip or card) — add the back if your document has one"
        : "Enter your National ID number",
      requiresBack: isDocumentFlow ? "optional" : false
    };
  }

  // Face liveness / comparison
  if (u.includes("LIVENESS") || u.includes("FACE COMPARISON") || u.includes("FACE MATCH"))
    return { icon: "🤳", desc: "We'll capture a short selfie video to verify you", requiresBack: false };

  return {
    icon: isDocumentFlow ? "📄" : "🆔",
    desc: isDocumentFlow ? "Capture or upload your document" : "Enter your ID number",
    requiresBack: false,
  };
}

// ─────────────────────────────────────────────────────────────────────
// ── Widget-side message sanitizer ─────────────────────────────────────────────
// Strips anything technical before showing text to the end user.
// Keeps messages that are already written plainly in natural language.
function _sanitizeUserMessage(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();

  // Block anything that looks internal / technical
  const blocklist = [
    "smartcomply", "face comparison service", "session id", "session_id",
    "provider", "ocr", "textract", "rekognition", "deepface",
    "ivs", "celery", "exception", "traceback", "str(e)",
    "error:", "django", "python", "http ",
    "comparison mode", "liveness mode", "callback",
    "face_comparison", "face_match", "confidence_percentage",
    "threshold_percentage", "polling_fallback", "resolved_by",
    // Billing/wallet messages are an operator problem (fund the account,
    // configure billing) — never the end user's to act on.
    "wallet", "fund your wallet", "billing is not configured",
    "insufficient balance", "insufficient fund",
  ];
  for (const blocked of blocklist) {
    if (lower.includes(blocked)) return null;
  }

  // Block a bare exception-style "Failed:<detail>" (no space after the colon,
  // e.g. "AuthenticationFailed:invalid_token") but NOT the backend's
  // deliberate natural-language shape "{Identity Type} check failed: {reason}"
  // (e.g. "Bank Verification Number Advanced check failed: name does not
  // match") — that phrasing is written for end users and is exactly what
  // should surface, not get discarded.
  if (/failed:\S/i.test(raw)) return null;

  // Block snake_case (internal field names accidentally leaking through)
  if (/[a-z]+_[a-z]+/.test(raw) && !/\s/.test(raw.trim())) return null;

  // Block anything starting with a lowercase letter (likely a code/key)
  if (raw.trim()[0] === raw.trim()[0].toLowerCase() && /^[a-z]/.test(raw.trim())) return null;

  return raw.trim();
}

// ── B8: OCR date string normaliser ───────────────────────────────────────────
// OCR providers return dates in wildly different formats. new Date(str) is
// locale-dependent for anything other than ISO 8601 (YYYY-MM-DD), so we
// attempt a set of known patterns before falling back to the native parser.
function _parseOcrDate(raw: string): Date | null {
  if (!raw) return null;
  const s = raw.trim();

  // ISO 8601 or near-ISO  — try first, always unambiguous
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s.slice(0, 10));

  // DD/MM/YYYY  or  DD-MM-YYYY
  const dmy = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})/);
  if (dmy) return new Date(`${dmy[3]}-${dmy[2]}-${dmy[1]}`);

  // MM/DD/YYYY  (US format — less common from OCR but possible)
  const mdy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (mdy) {
    // Heuristic: if "month" > 12 it must be day-first
    if (parseInt(mdy[1], 10) > 12) return new Date(`${mdy[3]}-${mdy[2]}-${mdy[1]}`);
    return new Date(`${mdy[3]}-${mdy[1]}-${mdy[2]}`);
  }

  // DD Mon YYYY  e.g. "12 May 2029"  or  "12 MAY 2029"
  const dMonY = s.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
  if (dMonY) return new Date(`${dMonY[2]} ${dMonY[1]} ${dMonY[3]}`);

  // Last resort: native parser
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// ── Document type code → human label ─────────────────────────────────────────
function _formatDocType(code: string): string {
  const map: Record<string, string> = {
    passport:          "Passport",
    national_id:       "National ID",
    nin_slip:          "NIN Slip",
    nin_with_face:     "National Identity Number",
    drivers_license:   "Driver's Licence",
    voters_card:       "Voter's Card",
    kenya_id:          "Kenya National ID",
    ghana_id:          "Ghana Card",
  };
  if (!code) return "";
  const key = code.toLowerCase().replace(/[\s-]/g, "_");
  if (map[key]) return map[key];
  // Fallback: title-case the code
  return code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// SmartComplyFlow
// ─────────────────────────────────────────────────────────────────────

export class SmartComplyFlow {
  private sdk: SmartComply;
  private options: FlowOptions;
  private theme: ThemeColors = getTheme("default");

  // DOM
  private overlay: HTMLDivElement | null = null;
  private modal: HTMLDivElement | null = null;
  private contentArea: HTMLDivElement | null = null;
  private progressBar: HTMLDivElement | null = null;
  private stepLabel: HTMLDivElement | null = null;
  private stageDots: HTMLDivElement | null = null;
  private brandLabel: HTMLDivElement | null = null;
  private brandLogoImg: HTMLImageElement | null = null;

  // State
  private sdkConfig: SDKInitConfig | null = null;
  private currentStep: StepName = "loading";
  private selectedCountry: string = "";
  private selectedChannelId: number = 0;
  private selectedChannelFields: { type: string; label: string; options?: string[] }[] = [];
  private selectedIdType: string = "";  // Canonical code (e.g., "national_id", "nin_with_face")
  private selectedIdTypeName: string = "";  // Display name (e.g., "National Identity Number with Face (NIN)")
  private selectedRequiresBack: BackSideMode | undefined = undefined; // Backend-provided back-side flag
  private collectedFields: Record<string, string> = {};
  private idNumber: string = "";
  private documentBlob: Blob | null = null;
  private documentBackBlob: Blob | null = null;
  // Whether the document's front image came from the live camera vs. a
  // gallery/file upload — gates whether pre-warming the liveness camera on
  // the confirm screens makes sense (reusing an already-open camera) or
  // would surprise a user who hasn't touched their camera yet this session.
  private _documentViaCamera = false;
  private identityCheckId: number | null = null;
  private verificationResult: VerifyIdentityResponse | null = null;
  private livenessEntryId: number | null = null;
  private isDestroyed = false;
  private headerBackBtn: HTMLButtonElement | null = null;
  private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private _visibilityHandler: (() => void) | null = null;
  private _hiddenAtMs: number | null = null;
  // Set just before a re-background restart re-renders the liveness step
  // (see the visibilitychange handler below), read once by renderLiveness
  // to show a "your scan was interrupted" notice instead of silently
  // swapping back into a fresh scan with no explanation.
  private _restartedAfterBackground = false;
  private _popstateHandler: (() => void) | null = null;
  private _historyGuardActive = false;

  // Retry safety — max 3 confirmation rejections or liveness failures per session
  private _confirmRetries = 0;
  private _livenessFailures = 0;
  private _livenessAttemptId = 0;
  private static readonly MAX_RETRIES = 3;

  // IVS result stored for confirm_identity screen
  private _ivsName: string = "";
  private _ivsGender: string = "";

  // OCR result stored for document_confirm screen
  private _ocrName: string = "";
  private _ocrDocNumber: string = "";
  private _ocrExpiry: string = "";
  private _ocrFaceImageUrl: string = "";
  private _ocrIsExpired: boolean = false;
  private _ocrExpirySoon: boolean = false;

  // Camera pre-warm stream (created during confirmation screen, used in liveness)
  private _prewarmedStream: MediaStream | null = null;

  // OCR gate poll timer — stored so it can be cancelled on navigation or close
  private _ocrPollTimer: ReturnType<typeof setTimeout> | null = null;

  // UI timeout from _startStatusPolling — stored so close() can cancel it
  private _uiTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Session expiry
  private sessionExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionExpiredTimer: ReturnType<typeof setTimeout> | null = null;

  // Status polling (result screen)
  private _pollStatusTimer: ReturnType<typeof setTimeout> | null = null;

  // Components
  private docCapture: DocumentCapture | null = null;

  private constructor(options: FlowOptions) {
    this.options = options;
    this.sdk = new SmartComply({
      apiKey: options.apiKey,
      clientId: options.clientId,
      environment: options.environment || "sandbox",
      timeout: options.timeout,
    });
  }

  /**
   * Open the SmartComply verification flow.
   *
   * Usage:
   *   SmartComplyFlow.open({
   *     apiKey: "pk_live_...",
   *     clientId: "uuid-...",
   *     environment: "production",
   *     onComplete: (result) => console.log("Done!", result),
   *     onError: (err) => console.error(err),
   *     onClose: () => console.log("Closed"),
   *   });
   */
  static open(options: FlowOptions): SmartComplyFlow {
    const flow = new SmartComplyFlow(options);
    flow.mount();
    flow.initialize();
    return flow;
  }

  /**
   * Close and destroy the flow widget.
   */
  close(): void {
    this.isDestroyed = true;
    if (this._keydownHandler) {
      document.removeEventListener("keydown", this._keydownHandler);
      this._keydownHandler = null;
    }
    if (this._visibilityHandler) {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
      this._visibilityHandler = null;
    }
    if (this._popstateHandler) {
      window.removeEventListener("popstate", this._popstateHandler);
      this._popstateHandler = null;
    }
    // Closing via the ✕/Escape/completion (not via the back gesture, which
    // handles its own guard entry above) still leaves our one guard entry
    // sitting on top of the host's history — back out of it so the user
    // isn't left pressing back once "into" a widget that's already gone.
    if (this._historyGuardActive) {
      this._historyGuardActive = false;
      try {
        if (history.state?.scFlowGuard) history.back();
      } catch { /* ignore — best effort only */ }
    }
    this.docCapture?.destroy();
    // Release any pre-warmed camera stream
    if (this._prewarmedStream) {
      this._prewarmedStream.getTracks().forEach(t => t.stop());
      this._prewarmedStream = null;
    }
    if (this.sessionExpiryTimer) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = null;
    }
    if (this.sessionExpiredTimer) {
      clearTimeout(this.sessionExpiredTimer);
      this.sessionExpiredTimer = null;
    }
    if (this._pollStatusTimer) {
      clearTimeout(this._pollStatusTimer);
      this._pollStatusTimer = null;
    }
    // B6: cancel OCR gate poll timer
    if (this._ocrPollTimer) {
      clearTimeout(this._ocrPollTimer);
      this._ocrPollTimer = null;
    }
    // P4: cancel result-screen UI timeout
    if (this._uiTimeoutId) {
      clearTimeout(this._uiTimeoutId);
      this._uiTimeoutId = null;
    }

    if (this.overlay) {
      this.overlay.style.opacity = "0";
      setTimeout(() => {
        try {
          this.overlay?.remove();
        } catch (_) { /* extension interference — safe to ignore */ }
        this.overlay = null;
        this.modal = null;
        this.contentArea = null;
      }, 300);
    }

    this.options.onClose?.();
  }

  // ─────────────────────────────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────────────────────────────

  private mount(): void {
    // Inject keyframes
    this.injectStyles();

    // Overlay
    this.overlay = document.createElement("div");
    this.overlay.id = "sc-flow-overlay";
    this.overlay.style.cssText = `
      position:fixed;inset:0;z-index:99999;
      display:flex;align-items:center;justify-content:center;
      background:${this.theme.overlay};
      backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);
      opacity:0;transition:opacity 0.3s ease;
      font-family:'Inter','Segoe UI',system-ui,-apple-system,sans-serif;
    `;

    // Modal
    this.modal = document.createElement("div");
    this.modal.id = "sc-flow-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.modal.setAttribute("aria-label", "Identity verification");
    this.modal.style.cssText = `
      width:100%;max-width:580px;max-height:95vh;
      background:${this.theme.bg};
      border-radius:20px;overflow:hidden;
      box-shadow:${this.theme.shadow};
      display:flex;flex-direction:column;
      transform:translateY(20px) scale(0.97);
      transition:transform 0.35s cubic-bezier(0.16,1,0.3,1);
    `;

    // Header
    const header = this.createHeader();
    this.modal.appendChild(header);

    // Progress bar
    const progressWrap = document.createElement("div");
    progressWrap.style.cssText = `height:3px;background:${this.theme.shimmer};`;
    this.progressBar = document.createElement("div");
    this.progressBar.style.cssText = `
      height:100%;width:0%;background:linear-gradient(90deg,${this.theme.primary},${this.theme.primaryHover});
      border-radius:0 2px 2px 0;transition:width 0.5s ease;
    `;
    progressWrap.appendChild(this.progressBar);
    this.modal.appendChild(progressWrap);

    // Content area (scrollable)
    this.contentArea = document.createElement("div");
    this.contentArea.id = "sc-flow-content";
    this.contentArea.setAttribute("role", "main");
    this.contentArea.setAttribute("aria-live", "polite");
    this.contentArea.setAttribute("aria-atomic", "false");
    this.contentArea.style.cssText = `
      flex:1;overflow-y:auto;padding:16px 20px;
      -webkit-overflow-scrolling:touch;
    `;
    this.modal.appendChild(this.contentArea);

    // Footer
    const footer = this.createFooter();
    this.modal.appendChild(footer);

    this.overlay.appendChild(this.modal);
    document.body.appendChild(this.overlay);

    // Animate in
    requestAnimationFrame(() => {
      if (this.overlay) this.overlay.style.opacity = "1";
      if (this.modal) this.modal.style.transform = "translateY(0) scale(1)";
    });

    // Escape closes the dialog; Tab is trapped inside it (standard WCAG dialog pattern).
    this._keydownHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === "Tab" && this.modal) {
        const focusable = this.modal.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", this._keydownHandler);

    // A tab backgrounded mid-scan (a notification, switching apps to copy a
    // code, a phone call) leaves the camera stream and detection loop in an
    // undefined state on most mobile browsers — rAF throttles/pauses while
    // hidden, and some browsers suspend the camera outright. Rather than let
    // the scan silently continue against stale state and fail confusingly,
    // restart the liveness step cleanly once the tab has been away long
    // enough to plausibly have disrupted an active camera session. A brief
    // glance elsewhere (quick app switch, screen lock tap) is not enough to
    // warrant restarting — only a real return-from-away.
    const REBACKGROUND_RESTART_MS = 3000;
    this._visibilityHandler = () => {
      if (this.isDestroyed) return;
      if (document.hidden) {
        this._hiddenAtMs = Date.now();
        return;
      }
      const hiddenAt = this._hiddenAtMs;
      this._hiddenAtMs = null;
      if (hiddenAt === null) return;
      const awayMs = Date.now() - hiddenAt;
      if (awayMs >= REBACKGROUND_RESTART_MS && this.currentStep === "liveness") {
        this._restartedAfterBackground = true;
        this.showStep("liveness");
      }
    };
    document.addEventListener("visibilitychange", this._visibilityHandler);

    // Intercept the browser/OS back gesture so it steps back within the flow
    // instead of navigating the host page away and abandoning the session
    // mid-verification (camera left open, timers orphaned). One guard entry
    // is pushed — not one per step — to minimize interference with a host
    // page's own router; popstate re-arms it each time so back can be
    // pressed repeatedly. If the current step has no back target, back
    // closes the widget cleanly instead (same as the header ✕).
    this._historyGuardActive = true;
    try {
      history.pushState({ scFlowGuard: true }, "");
    } catch {
      this._historyGuardActive = false; // host disallows pushState — degrade silently
    }
    this._popstateHandler = () => {
      if (this.isDestroyed || !this._historyGuardActive) return;
      const backAction = this._getBackAction(this.currentStep);
      if (backAction) {
        backAction();
      } else {
        this.close();
        return; // don't re-arm — we're closing, let the real back navigation proceed
      }
      try {
        history.pushState({ scFlowGuard: true }, "");
      } catch { /* ignore — nothing more we can do */ }
    };
    window.addEventListener("popstate", this._popstateHandler);
  }

  private async initialize(): Promise<void> {
    this.showStep("loading");

    try {
      if (this.options.resumeToken) {
        // A resume_token IS already a valid session on the backend — no
        // creation call, and no client-side expiry warning to schedule
        // (see resumeSession()'s own doc comment for why). The backend
        // still enforces the real expiry regardless; a request made after
        // it has genuinely lapsed is rejected there, same as any session.
        this.sdk.resumeSession(this.options.resumeToken);
      } else {
        await this.sdk.createSession();

        // C4: Start session expiry timer (warn 2 min before 30-min expiry)
        const expiresAt = this.sdk.sessionExpiresAt;
        if (expiresAt) {
          const expiresMs = new Date(expiresAt).getTime() - Date.now();
          const warnMs = Math.max(expiresMs - 120000, 0); // warn 2 min before
          this.sessionExpiryTimer = setTimeout(
            () => this._showExpiryWarningWhenSafe(),
            warnMs
          );
          // The warning above is advisory only and can defer indefinitely
          // while the user sits on a camera-active step — without a hard
          // stop, an ignored/deferred warning left the widget open against
          // an already-expired backend session (SessionToken.expires_at,
          // 30 min — see sdk/views.py's SessionToken.objects.create call)
          // for as long as the host page stayed open. Force-close once the
          // real deadline passes so the widget's lifetime never exceeds
          // what the backend actually honors.
          this.sessionExpiredTimer = setTimeout(
            () => this._enforceSessionExpiry(),
            Math.max(expiresMs, 0)
          );
        }
      }

      // Load config
      this.sdkConfig = await this.sdk.initializeConfig();

      // Apply theme from config — use theme_hex to override primary colour if set
      if (this.sdkConfig.theme) {
        this.theme = getTheme(this.sdkConfig.theme, (this.sdkConfig as any).theme_hex || null);
        this.applyTheme();
      }

      // Update brand name in header
      if (this.brandLabel && this.sdkConfig.brand_name) {
        this.brandLabel.textContent = this.sdkConfig.brand_name;
      }

      // Show the client's logo if this config has one — src is only set
      // once a URL exists, so the "error" fallback wired up in
      // createHeader() never fires from an empty/undefined src.
      if (this.brandLogoImg && this.sdkConfig.logo_url) {
        this.brandLogoImg.src = this.sdkConfig.logo_url;
        this.brandLogoImg.style.display = "block";
      }

      if (this.isDestroyed) return;

      if (this.options.resumeToken) {
        // null means _resolveResumeStep already rendered an error itself
        // (a provably expired/invalid resume link — see that method) and
        // there is no step left to show; calling showStep() in that case
        // would wipe the error message straight back out.
        const resumeStep = await this._resolveResumeStep();
        if (resumeStep !== null) this.showStep(resumeStep);
      } else {
        this.showStep("welcome");
      }

    } catch (err: any) {
      this.showErrorStep(err.message || "Failed to initialize. Please try again.");
    }
  }

  // Resolves a resume_token to the step the customer should land on,
  // pre-selecting the same country/ID type the original attempt used (the
  // same state renderCountrySelect/renderIdTypeSelect's own click handlers
  // set) so document_capture/liveness render correctly instead of assuming
  // state that was never set. Falls back to "welcome" on anything that
  // doesn't resolve cleanly for an UNCERTAIN reason (network error, timeout)
  // — this is a UX improvement, not a requirement: the backend's own
  // resume-match at create/ time still works correctly even if the widget
  // can't figure out which screen to pre-select.
  //
  // Returns null instead when the lookup fails for a CERTAIN, known reason
  // — the resume link itself is provably expired or already used
  // (RESUME_TARGET_UNAVAILABLE) or was never a resume link at all
  // (NOT_A_RESUME_TOKEN). Unlike a transient failure, there is no "try the
  // real request and it'll still work" fallback for these: create/'s own
  // resume-match reads the exact same resume_target_entry binding this
  // lookup just found missing/invalid, so falling through to a generic
  // welcome screen would silently walk the customer into a brand-new,
  // unrelated verification attempt with no explanation — worse than telling
  // them plainly that their link no longer works. The caller shows this
  // error itself (via showErrorStep, called here directly) rather than
  // rendering a step.
  private async _resolveResumeStep(): Promise<StepName | null> {
    try {
      const info = await this.sdk.getResumeInfo();

      if (
        info.verification_type === "liveness_only" ||
        info.failure_stage === "liveness" ||
        // data_match is always a selfie-vs-identity-photo mismatch (see the
        // backend's own comment in _process_identity_face_match) — fixed by
        // redoing the selfie/video, same as a "liveness" failure. There is
        // no document step in data verification at all, so this can never
        // legitimately need document_capture.
        info.failure_stage === "data_match"
      ) {
        // Nothing to re-select — there's no document channel to pick, just
        // liveness itself to redo. selectedCountry still needs a non-blank
        // value (renderLiveness sends it as a required field) the same way
        // the liveness-only welcome-screen path already handles this.
        this.selectedCountry = info.country || this.selectedCountry || "global";
        // A resume link is typically opened in a fresh tab/session (from an
        // email or SMS), which starts with identityCheckId at its default
        // null — it is only ever set in-memory by the onboarding/verify
        // step (see above) and was never carried over here. Without this,
        // a data-verification entry that failed liveness would resubmit
        // with identity_check omitted, silently dropping the link this
        // entry's identifier/identifier_type depend on (isDataVerification
        // in renderLiveness would read false even though this WAS a data
        // verification). Restore it whenever the backend reports one.
        this.identityCheckId = info.identity_check_id ?? null;
        return "liveness";
      }

      if (info.failure_stage === "document" && info.country && info.identifier_type) {
        // A document-stage resume always references a document_verification
        // channel by its doc code — channels[country] is now split by
        // category (see ChannelsByCategory), so search that list directly.
        const channels = this.sdkConfig?.channels || {};
        const idTypes = channels[info.country]?.document_verification || [];
        const match = idTypes.find((idType) => {
          const code = typeof idType === "string" ? idType : (idType.code || idType.name);
          return code === info.identifier_type;
        });

        if (match) {
          const typeName = typeof match === "string" ? match : match.name;
          const typeCode = typeof match === "string" ? match : (match.code || match.name);
          const channelFields = typeof match !== "string" ? (match.fields || []) : [];
          const isDocFlow = channelFields.some((f) => isUploadFieldType(f.type)) || this.isDocumentFlow();
          const info2 = resolveIdTypeInfo(typeName, isDocFlow);
          const requiresBackFromBackend =
            typeof match !== "string" ? (match as SDKChannel).requires_back_side : undefined;

          this.selectedCountry = info.country;
          this.selectedIdType = typeCode;
          this.selectedIdTypeName = typeName;
          this.selectedRequiresBack = requiresBackFromBackend ?? info2.requiresBack;
          if (typeof match !== "string") {
            this.selectedChannelId = match.id;
            this.selectedChannelFields = match.fields || [];
          }
          return "document_capture";
        }
        // Channel from the original submission no longer exists in this
        // config (e.g. it was disabled since) — fall through to welcome
        // rather than render document_capture with no title/back-side info.
      }

      // Nothing above matched cleanly (e.g. a document channel that no
      // longer exists in this config) — the entry is still correctly bound
      // server-side regardless of which screen the widget shows first.
      return "welcome";
    } catch (err) {
      const code = err instanceof SDKError ? err.errorCode : undefined;
      if (code === "RESUME_TARGET_UNAVAILABLE" || code === "NOT_A_RESUME_TOKEN") {
        // A certain, known failure — this specific link is provably no
        // longer usable (already used, expired, or was never a resume link
        // at all). Tell the customer plainly rather than silently starting
        // a new, unrelated verification on a welcome screen with no
        // explanation — see this method's own docstring above.
        this.showErrorStep("This resume link has expired or is no longer valid.");
        return null;
      }
      // Anything else (network error, timeout, an SDKError with a
      // different/absent code) — never block the flow on this lookup.
      // create/'s own resume-match still runs correctly once the customer
      // gets there, so falling through to a generic welcome screen is safe.
      return "welcome";
    }
  }

  // Defers the expiry warning while the user is on a camera-active step
  // (document_capture/liveness) — showing it there would blow away a live
  // video element and in-progress MediaRecorder mid-scan.
  private _showExpiryWarningWhenSafe(): void {
    if (this.isDestroyed) return;
    const cameraActiveSteps: StepName[] = ["document_capture", "liveness"];
    if (cameraActiveSteps.includes(this.currentStep)) {
      this.sessionExpiryTimer = setTimeout(() => this._showExpiryWarningWhenSafe(), 5000);
      return;
    }
    this.showErrorStep("Your session is about to expire. Please complete verification quickly or restart.");
  }

  // Hard stop once the backend session (SessionToken.expires_at, 30 min)
  // has actually passed. Deferred the same way as the warning above while
  // a camera is live so we don't tear down an in-progress scan, but capped
  // — the earlier warning already gave the user 2 minutes' notice, so
  // there's no reason to keep deferring past a short grace window the way
  // the advisory warning does.
  private static readonly MAX_EXPIRY_DEFERRALS = 12; // 12 * 5s = 60s grace
  private _expiryDeferrals = 0;
  private _enforceSessionExpiry(): void {
    if (this.isDestroyed) return;
    const cameraActiveSteps: StepName[] = ["document_capture", "liveness"];
    if (
      cameraActiveSteps.includes(this.currentStep) &&
      this._expiryDeferrals < SmartComplyFlow.MAX_EXPIRY_DEFERRALS
    ) {
      this._expiryDeferrals += 1;
      this.sessionExpiredTimer = setTimeout(() => this._enforceSessionExpiry(), 5000);
      return;
    }
    this.showErrorStep("Your session has expired. Please restart verification.");
    this.close();
  }

  // ─────────────────────────────────────────────────────────────────
  // Step rendering
  // ─────────────────────────────────────────────────────────────────

  private showStep(step: StepName): void {
    this.currentStep = step;
    if (!this.contentArea) return;

    // P1/B6: cancel any in-flight OCR gate poll when user navigates away
    if (this._ocrPollTimer) {
      clearTimeout(this._ocrPollTimer);
      this._ocrPollTimer = null;
    }

    // Release a pre-warmed camera stream if we're navigating anywhere other
    // than liveness (the only step that consumes it) — otherwise "No, use a
    // different ID" / "No, retake document" / header back-button leave the
    // camera held open indefinitely with no consumer.
    if (step !== "liveness" && this._prewarmedStream) {
      this._prewarmedStream.getTracks().forEach(t => t.stop());
      this._prewarmedStream = null;
    }

    // Update progress
    this.updateProgress();

    // Animate out current content
    const oldContent = this.contentArea.firstChild as HTMLElement | null;
    if (oldContent) {
      oldContent.style.cssText += "opacity:0;transform:translateX(-20px);transition:all 0.2s ease;";
      setTimeout(() => {
        if (this.contentArea) this.contentArea.innerHTML = "";
        this.renderStep(step);
      }, 200);
    } else {
      this.renderStep(step);
    }
  }

  private renderStep(step: StepName): void {
    if (!this.contentArea) return;

    const wrapper = document.createElement("div");
    wrapper.style.cssText = "opacity:0;transform:translateX(20px);transition:all 0.3s ease;";

    switch (step) {
      case "loading":
        this.renderLoading(wrapper);
        break;
      case "welcome":
        this.renderWelcome(wrapper);
        break;
      case "country":
        this.renderCountrySelect(wrapper);
        break;
      case "id_type":
        this.renderIdTypeSelect(wrapper);
        break;
      case "id_input":
        this.renderIdInput(wrapper);
        break;
      case "confirm_identity":
        this.renderConfirmIdentity(wrapper);
        break;
      case "document_capture":
        this.renderDocumentCapture(wrapper);
        break;
      case "ocr_gate":
        this.renderOcrGate(wrapper);
        break;
      case "document_confirm":
        this.renderDocumentConfirm(wrapper);
        break;
      case "liveness":
        this.renderLiveness(wrapper);
        break;
      case "done":
        this.renderDone(wrapper);
        break;
      case "error":
        break;
    }

    this.contentArea.appendChild(wrapper);

    // Animate in
    requestAnimationFrame(() => {
      wrapper.style.opacity = "1";
      wrapper.style.transform = "translateX(0)";
    });

    // Move focus to the new step's heading so keyboard/screen-reader users
    // aren't left on a now-detached button. Skipped for camera-active steps
    // and id_input, which already focuses its first field itself.
    const skipAutoFocusSteps: StepName[] = ["document_capture", "liveness", "id_input"];
    if (!skipAutoFocusSteps.includes(step)) {
      requestAnimationFrame(() => {
        const heading = wrapper.querySelector<HTMLElement>("h1, h2, h3");
        const target =
          heading ||
          wrapper.querySelector<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
        if (target) {
          if (heading && !heading.hasAttribute("tabindex")) {
            heading.setAttribute("tabindex", "-1");
          }
          target.focus({ preventScroll: true });
        }
      });
    }
  }

  // ── Loading ─────────────────────────────────────────────────────

  private renderLoading(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:300px;gap:16px;";

    container.appendChild(renderSpinner(this.theme, { size: 60, icon: icon("shield", 22) }));

    const label = document.createElement("div");
    label.style.cssText = `color:${this.theme.text};font-size:${TYPE.base}px;font-weight:600;`;
    label.textContent = "Setting up secure session";
    container.appendChild(label);

    const sub = document.createElement("div");
    sub.style.cssText = `color:${this.theme.textMuted};font-size:12px;text-align:center;max-width:220px;line-height:1.5;`;
    sub.textContent = "Connecting to the verification service...";
    container.appendChild(sub);
  }

  // ── Welcome ────────────────────────────────────────────────────

  private renderWelcome(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:12px;";

    const isLivenessOnly = this.isLivenessOnlyFlow();
    const isDocFlow = isLivenessOnly ? false : this.isDocumentFlow();

    // Icon + badge row (side by side to save vertical space)
    const topRow = document.createElement("div");
    topRow.style.cssText = "display:flex;flex-direction:column;align-items:center;gap:8px;";

    const iconWrap = document.createElement("div");
    iconWrap.style.cssText = `
      width:50px;height:50px;border-radius:14px;
      display:flex;align-items:center;justify-content:center;
      color:${this.theme.primary};
      background:linear-gradient(135deg,${this.theme.primary}22,${this.theme.primaryGlow});
      border:1.5px solid ${this.theme.primary}33;
      overflow:hidden;
    `;
    // Client's logo takes over this hero spot when set — otherwise the
    // generic shield/id-card mark that every tenant used to see regardless
    // of who they were.
    const logoUrl = this.sdkConfig?.logo_url;
    if (logoUrl) {
      const logoImg = document.createElement("img");
      logoImg.src = logoUrl;
      logoImg.alt = "";
      logoImg.style.cssText = "width:100%;height:100%;object-fit:contain;padding:6px;";
      // Same fallback as the header logo — a broken image here is worse
      // than the generic icon it would otherwise have shown.
      logoImg.addEventListener("error", () => {
        iconWrap.innerHTML = icon(isDocFlow ? "id-card" : "shield", 24);
      });
      iconWrap.appendChild(logoImg);
    } else {
      iconWrap.innerHTML = icon(isDocFlow ? "id-card" : "shield", 24);
    }
    topRow.appendChild(iconWrap);

    const badge = document.createElement("div");
    badge.style.cssText = `
      display:inline-flex;align-items:center;gap:5px;
      padding:3px 11px;border-radius:999px;
      font-size:10.5px;font-weight:600;letter-spacing:0.2px;
      background:${this.theme.primary}15;
      color:${this.theme.primary};
      border:1px solid ${this.theme.primary}30;
    `;
    badge.textContent = isLivenessOnly
      ? "🤳  Liveness Check"
      : isDocFlow ? "📄  Document Verification" : "🔍  Data Verification";
    topRow.appendChild(badge);
    container.appendChild(topRow);

    // Title + subtitle (compact)
    const titleWrap = document.createElement("div");
    titleWrap.style.cssText = "display:flex;flex-direction:column;align-items:center;gap:5px;width:100%;";

    const title = document.createElement("h2");
    title.style.cssText = `
      color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;
      margin:0;line-height:1.2;
    `;
    // W2: Use client brand name in title
    const brandName = this.sdkConfig?.brand_name;
    title.textContent = brandName
      ? `${brandName} Identity Verification`
      : "Verify Your Identity";
    titleWrap.appendChild(title);

    const desc = document.createElement("p");
    desc.style.cssText = `
      color:${this.theme.textSecondary};font-size:13px;line-height:1.5;
      margin:0 auto;max-width:300px;text-align:center;
    `;
    desc.textContent = isLivenessOnly
      ? "You'll just need camera access. This takes about 30 seconds."
      : isDocFlow
        ? "You'll need your document and camera access. This takes about 2 minutes."
        : "You'll need your ID number and camera access. This takes about 2 minutes.";
    titleWrap.appendChild(desc);
    container.appendChild(titleWrap);

    // Steps preview — compact rows
    const steps = isLivenessOnly
      ? [
          { icon: "🤳", text: "Face check", sub: "Quick selfie to confirm it's you" },
        ]
      : isDocFlow
        ? [
            { icon: "🪪", text: "Choose your document type", sub: "Passport, National ID, Driver License" },
            { icon: "📷", text: "Scan your document", sub: "Upload or capture a photo of your ID" },
            { icon: "🤳", text: "Face check", sub: "Quick selfie to confirm it's you" },
          ]
        : [
            { icon: "🆔", text: "Choose your ID type", sub: "NIN, BVN, Voter's Card and more" },
            { icon: "📝", text: "Enter your ID details", sub: "Exactly as shown on your ID" },
            { icon: "🤳", text: "Face check", sub: "Quick selfie to confirm it's you" },
          ];

    const stepsWrap = document.createElement("div");
    stepsWrap.style.cssText = `
      width:100%;display:flex;flex-direction:column;gap:0;
      border-radius:${RADIUS.md}px;overflow:hidden;
      border:1.5px solid ${this.theme.border};
    `;

    steps.forEach((step, i) => {
      const row = document.createElement("div");
      row.style.cssText = `
        display:flex;align-items:center;gap:12px;
        padding:8px 14px;
        background:${this.theme.cardBg};
        ${i < steps.length - 1 ? `border-bottom:1px solid ${this.theme.border};` : ""}
      `;

      const iconEl = document.createElement("div");
      iconEl.style.cssText = `
        width:28px;height:28px;border-radius:${RADIUS.sm}px;flex-shrink:0;
        display:flex;align-items:center;justify-content:center;
        font-size:13px;
        background:${this.theme.primary}12;
        border:1px solid ${this.theme.primary}20;
      `;
      iconEl.textContent = step.icon;

      const textWrap = document.createElement("div");
      textWrap.style.cssText = "display:flex;flex-direction:column;gap:1px;text-align:left;";

      const textEl = document.createElement("span");
      textEl.style.cssText = `color:${this.theme.text};font-size:12.5px;font-weight:600;`;
      textEl.textContent = step.text;

      const subEl = document.createElement("span");
      subEl.style.cssText = `color:${this.theme.textMuted};font-size:10.5px;`;
      subEl.textContent = step.sub;

      textWrap.appendChild(textEl);
      textWrap.appendChild(subEl);
      row.appendChild(iconEl);
      row.appendChild(textWrap);
      stepsWrap.appendChild(row);
    });

    container.appendChild(stepsWrap);

    // CTA
    const btn = this.createPrimaryButton("Get Started →");
    btn.style.cssText += "margin-top:2px;";
    btn.addEventListener("click", () => {
      if (isLivenessOnly) {
        // Liveness-only has no channels at all, so renderCountrySelect's
        // single-country auto-skip (countries.length === 1) never fires for
        // it — going through "country" here would render an empty,
        // dead-end country-picker screen. Go straight to liveness instead.
        //
        // selectedCountry defaults to "" and is otherwise only ever set by
        // renderCountrySelect, which this mode never visits — renderLiveness
        // sends it as a required, non-blank `country` field, so leaving it
        // empty would fail create() validation before the scan even starts.
        // There's no real country to report here (no identity lookup runs
        // in this mode), so this is a neutral placeholder, not a claim
        // about the user's actual location.
        this.selectedCountry = this.selectedCountry || "global";
        this.showStep("liveness");
      } else {
        this.showStep("country");
      }
    });
    container.appendChild(btn);

    // Plain-language line on what the camera/photo is for and what happens
    // to it — shown before the "Get Started" tap that leads to the first
    // camera permission prompt, so the browser's own prompt isn't the only
    // context the user gets for why a camera is being requested.
    const trust = document.createElement("div");
    trust.style.cssText = `
      display:flex;align-items:flex-start;gap:6px;text-align:left;
      color:${this.theme.textMuted};font-size:10.5px;line-height:1.5;
    `;
    trust.innerHTML = `<span style="flex-shrink:0;">🔒</span><span>Your photo and camera scan are used only to verify it's really you, and are kept encrypted.</span>`;
    container.appendChild(trust);
  }

  // ── Country Select ──────────────────────────────────────────────

  private renderCountrySelect(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;gap:14px;";

    const channels = this.sdkConfig?.channels || {};
    const countries = Object.keys(channels);

    // Auto-skip country selection if only one country available
    if (countries.length === 1) {
      this.selectedCountry = countries[0];
      this.showStep("id_type");
      return;
    }

    const title = this.createStepTitle("Select your country");
    container.appendChild(title);

    const subtitle = document.createElement("div");
    subtitle.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin-top:-8px;`;
    subtitle.textContent = "Choose the country that issued your ID";
    container.appendChild(subtitle);

    const list = document.createElement("div");
    list.setAttribute("role", "list");
    list.style.cssText = "display:flex;flex-direction:column;gap:10px;max-height:min(52vh,420px);overflow-y:auto;padding:2px;";

    const renderList = (query: string): void => {
      list.innerHTML = "";
      const matches = filterCountries(channels, query);
      if (matches.length === 0) {
        const empty = document.createElement("div");
        empty.setAttribute("role", "status");
        empty.style.cssText = `color:${this.theme.textSecondary};font-size:13px;line-height:1.6;padding:8px 2px;`;
        empty.textContent = `No country matches "${query.trim()}". Check the spelling or try another name.`;
        list.appendChild(empty);
        return;
      }
      for (const country of matches) {
        const count = channels[country].data_verification.length
          + channels[country].document_verification.length;
        const card = this.createOptionCard(
          countryLabel(channels, country),
          `${count} ID type${count > 1 ? "s" : ""} available`,
          () => {
            this.selectedCountry = country;
            this.showStep("id_type");
          }
        );
        card.setAttribute("role", "listitem");
        list.appendChild(card);
      }
    };

    if (countries.length >= COUNTRY_SEARCH_MIN) {
      const search = document.createElement("input");
      search.type = "search";
      search.placeholder = "Search countries";
      search.setAttribute("aria-label", "Search countries");
      search.autocomplete = "off";
      search.style.cssText = `
        width:100%;box-sizing:border-box;padding:11px 14px;border-radius:${RADIUS.md}px;
        background:${this.theme.cardBg};color:${this.theme.text};
        border:1.5px solid ${this.theme.border};font-size:14px;outline-offset:2px;
      `;
      search.addEventListener("input", () => renderList(search.value));
      container.appendChild(search);
    }

    renderList("");
    container.appendChild(list);
  }

  // ── ID Type Select ──────────────────────────────────────────────

  private renderIdTypeSelect(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;gap:14px;";

    const globalDocFlow = this.isDocumentFlow();

    const title = this.createStepTitle(globalDocFlow ? "Select document type" : "Select ID type");
    container.appendChild(title);

    const subtitle = document.createElement("div");
    subtitle.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin-top:-8px;`;
    subtitle.textContent = globalDocFlow
      ? "Choose the document you want to scan"
      : "Choose the ID you want to verify";
    container.appendChild(subtitle);

    const channels = this.sdkConfig?.channels || {};
    const vType = this.sdkConfig?.verification_type;
    const verificationTypes: string[] = !vType ? [] : Array.isArray(vType) ? vType : [vType];
    const idTypes = selectIdTypesForScreen(channels[this.selectedCountry], globalDocFlow, verificationTypes);

    if (idTypes.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = `color:${this.theme.textSecondary};font-size:13px;line-height:1.6;`;
      empty.textContent =
        "No ID types are set up for this country yet. Please go back and choose another country, or contact support.";
      container.appendChild(empty);
      return;
    }

    for (const idType of idTypes) {
      const typeName = typeof idType === "string" ? idType : idType.name;
      // Use code (canonical) for API calls, fallback to name for backward compatibility
      const typeCode = typeof idType === "string" ? idType : (idType.code || idType.name);
      // Detect per-channel, not from the config's overall mode — see
      // isChannelDocumentFlow's docstring for why.
      const isThisDocFlow = isChannelDocumentFlow(idType, globalDocFlow);
      const info = resolveIdTypeInfo(typeName, isThisDocFlow);

      // Prefer the backend-provided back-side flag; fall back to local heuristics only when absent.
      const requiresBackFromBackend =
        typeof idType !== "string" ? (idType as SDKChannel).requires_back_side : undefined;
      const requiresBack = requiresBackFromBackend ?? info.requiresBack;

      // Add back side indicator to subtitle if needed (only when strictly
      // required — "optional" already reads correctly from info.desc, e.g.
      // NIN's "back optional", and must not be overwritten with this warning).
      let subtitle = info.desc;
      if (requiresBack === true && isThisDocFlow) {
        subtitle += " ⚠️ Both sides required";
      }

      // Clean display name — strip "with face", "(NIN)", "(BVN)" from user-visible label
      const displayName = typeName
        .replace(/\s*with\s+face\s*/gi, ' ')
        .replace(/\s*\(NIN\)\s*/gi, ' ')
        .replace(/\s*\(BVN\)\s*/gi, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();

      const card = this.createOptionCard(
        `${info.icon}  ${displayName}`,
        subtitle,
        () => {
          const idTypeChanged = this.selectedIdType !== typeCode;
          this.selectedIdType = typeCode;  // Store canonical code for API
          this.selectedIdTypeName = typeName;  // Keep display name for UI
          this.selectedRequiresBack = requiresBack;
          // Always reset first to avoid stale state when user goes back and changes selection
          this.selectedChannelId = 0;
          this.selectedChannelFields = [];
          this.collectedFields = {};
          // Only discard an in-flight liveness/doc capture if the user actually changed ID type
          if (idTypeChanged) {
            this.livenessEntryId = null;
            this.documentBlob = null;
            this.documentBackBlob = null;
          }
          if (typeof idType !== "string") {
            this.selectedChannelId = idType.id;
            this.selectedChannelFields = idType.fields || []; // guard null/undefined from backend
          }
          // Route on THIS channel, same as isThisDocFlow above — see
          // isChannelDocumentFlow's docstring.
          const isDocChannel = isChannelDocumentFlow(idType, globalDocFlow);
          this.showStep(isDocChannel ? "document_capture" : "id_input");
        }
      );
      container.appendChild(card);
    }

  }

  // ── ID Input (data verification — dynamic fields) ─────────────

  private renderIdInput(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;gap:14px;";

    const dataFields = this.selectedChannelFields.filter((f) => f.type !== "upload");
    const info = resolveIdTypeInfo(this.selectedIdTypeName || this.selectedIdType, false);
    const cleanStepTitle = (this.selectedIdTypeName || this.selectedIdType)
      .replace(/\s*with\s+face\s*/gi, ' ')
      .replace(/\s*\(NIN\)\s*/gi, ' ')
      .replace(/\s*\(BVN\)\s*/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();

    const title = this.createStepTitle(`${info.icon}  ${cleanStepTitle}`);
    container.appendChild(title);

    const subtitle = document.createElement("div");
    subtitle.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin-top:-8px;`;
    subtitle.textContent = dataFields.length > 0
      ? `Enter your ${this.selectedIdTypeName || this.selectedIdType} details exactly as they appear on your ID.`
      : "Fill in the fields below to continue.";
    container.appendChild(subtitle);

    // Edge case: no data fields configured — show fallback and allow submission
    if (dataFields.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = `
        padding:16px;border-radius:10px;
        background:${this.theme.cardBg};border:1px solid ${this.theme.border};
        color:${this.theme.textSecondary};font-size:13px;text-align:center;
      `;
      empty.textContent = "No additional details required. Click below to continue.";
      container.appendChild(empty);
      const autoBtn = this.createPrimaryButton("Continue");
      autoBtn.addEventListener("click", () => this.showStep("liveness"));
      container.appendChild(autoBtn);
      return;
    }

    // ── Field format rules ────────────────────────────────────────────────
    // Keyed by uppercased label substring → { minLen, maxLen, numeric, pattern, hint }
    interface FieldRule {
      minLen?: number;
      maxLen?: number;
      numeric?: boolean;      // only digits allowed
      alphanumeric?: boolean; // letters + digits only
      pattern?: RegExp;
      hint: string;
      placeholder?: string;   // overrides the default "Enter {label}" placeholder
    }
    const _FIELD_RULES: Array<{ match: string; rule: FieldRule }> = [
      { match: "BVN",               rule: { minLen: 11, maxLen: 11, numeric: true,  hint: "BVN must be exactly 11 digits" } },
      { match: "NIN",               rule: { minLen: 11, maxLen: 11, numeric: true,  hint: "NIN must be exactly 11 digits" } },
      { match: "NATIONAL IDENTITY", rule: { minLen: 11, maxLen: 11, numeric: true,  hint: "NIN must be exactly 11 digits" } },
      { match: "PASSPORT",          rule: { minLen: 6,  maxLen: 20, alphanumeric: true, hint: "Passport number must be 6–20 alphanumeric characters" } },
      { match: "DRIVER",            rule: { minLen: 3,  maxLen: 30, alphanumeric: true, hint: "Licence number must be 3–30 alphanumeric characters" } },
      { match: "VOTER",             rule: { minLen: 6,  maxLen: 30, alphanumeric: true, hint: "Voter ID must be 6–30 alphanumeric characters" } },
      { match: "GHANA",             rule: { minLen: 8,  maxLen: 20, alphanumeric: true, hint: "Ghana Card number must be 8–20 alphanumeric characters" } },
      { match: "SSNIT",             rule: { minLen: 7,  maxLen: 15, alphanumeric: true, hint: "SSNIT number must be 7–15 alphanumeric characters" } },
      { match: "TIN",               rule: { minLen: 8,  maxLen: 20, alphanumeric: true, hint: "TIN must be 8–20 characters" } },
      { match: "KRA",               rule: { minLen: 10, maxLen: 12, alphanumeric: true, hint: "KRA PIN must be 10–12 characters" } },
      // Backend accepts DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD or YYYY/MM/DD
      // (see _normalize_date_str in ivs_run_check.py) — DD-MM-YYYY shown here
      // since it matches the date format printed on NIN/BVN records.
      { match: "DATE OF BIRTH",     rule: { pattern: /^\d{2}[-/]\d{2}[-/]\d{4}$|^\d{4}[-/]\d{2}[-/]\d{2}$/, hint: "Format: DD-MM-YYYY (e.g. 15-06-1996)", placeholder: "DD-MM-YYYY" } },
      { match: "DOB",               rule: { pattern: /^\d{2}[-/]\d{2}[-/]\d{4}$|^\d{4}[-/]\d{2}[-/]\d{2}$/, hint: "Format: DD-MM-YYYY (e.g. 15-06-1996)", placeholder: "DD-MM-YYYY" } },
    ];

    function getFieldRule(label: string): FieldRule | null {
      const upper = label.toUpperCase();
      for (const entry of _FIELD_RULES) {
        if (upper.includes(entry.match)) return entry.rule;
      }
      return null;
    }

    function validateFieldValue(label: string, value: string): string | null {
      const rule = getFieldRule(label);
      if (!rule) return null; // no rule = no validation
      const v = value.trim();
      if (rule.numeric && !/^\d+$/.test(v))       return `${label} must contain digits only`;
      if (rule.alphanumeric && !/^[A-Za-z0-9]+$/.test(v)) return `${label} must be alphanumeric (letters and digits only)`;
      if (rule.pattern && !rule.pattern.test(v))   return rule.hint;
      if (rule.minLen && v.length < rule.minLen)   return rule.hint;
      if (rule.maxLen && v.length > rule.maxLen)   return rule.hint;
      return null;
    }

    // Track input elements for validation
    const inputEls: { field: any; el: HTMLInputElement | HTMLSelectElement }[] = [];

    for (const field of dataFields) {
      // Send the raw label as the payload key — the backend's
      // _SDK_FIELD_KEY_MAP looks up the exact label text (e.g. "National ID",
      // "CAC Number") to remap it to the canonical IVS key. Pre-normalizing
      // it here (as this used to do) produced keys like "national_id" that
      // don't match the map's literal-label entries and silently fall
      // through to the wrong key for several identity types.
      const fieldKey = field.label;

      const fieldWrap = document.createElement("div");
      fieldWrap.style.cssText = "display:flex;flex-direction:column;gap:6px;";

      const labelId = `sc-field-label-${fieldKey.replace(/\s+/g, '-').toLowerCase()}`;
      const label = document.createElement("label");
      label.id = labelId;
      label.htmlFor = `sc-field-input-${fieldKey.replace(/\s+/g, '-').toLowerCase()}`;
      label.style.cssText = `color:${this.theme.textMuted};font-size:10px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;`;
      label.textContent = field.label;
      fieldWrap.appendChild(label);

      if (field.type === "dropdown" || field.type === "select") {
        // Dropdown field
        const select = document.createElement("select");
        select.style.cssText = `
          width:100%;padding:13px 16px;border-radius:${RADIUS.md}px;
          font-size:15px;
          background:${this.theme.inputBg};
          color:${this.theme.text};
          border:2px solid ${this.theme.border};
          outline:none;box-sizing:border-box;
          cursor:pointer;transition:border-color 0.2s ease;
        `;
        const placeholder = document.createElement("option");
        placeholder.value = "";
        placeholder.textContent = `Select ${field.label}`;
        placeholder.disabled = true;
        placeholder.selected = true;
        select.appendChild(placeholder);

        for (const opt of field.options || []) {
          const option = document.createElement("option");
          option.value = opt;
          option.textContent = opt;
          select.appendChild(option);
        }

        select.addEventListener("focus", () => { select.style.borderColor = this.theme.primary; });
        select.addEventListener("blur",  () => { select.style.borderColor = this.theme.border; });
        select.addEventListener("change", () => {
          this.collectedFields[fieldKey] = select.value;
          updateSubmitState();
        });
        fieldWrap.appendChild(select);
        inputEls.push({ field, el: select });
      } else if (field.type === "image" || field.type === "file") {
        // File upload field — converts to base64 for API submission
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*";
        fileInput.style.cssText = `
          width:100%;padding:12px 16px;border-radius:${RADIUS.md}px;
          font-size:14px;font-weight:500;
          background:${this.theme.inputBg};
          color:${this.theme.text};
          border:2px dashed ${this.theme.border};
          outline:none;transition:border-color 0.2s ease;
          box-sizing:border-box;cursor:pointer;
        `;
        fileInput.addEventListener("focus", () => {
          fileInput.style.borderColor = this.theme.primary;
        });
        fileInput.addEventListener("blur", () => {
          fileInput.style.borderColor = this.theme.border;
        });
        fileInput.addEventListener("change", () => {
          const file = fileInput.files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(",")[1] || "";
              this.collectedFields[fieldKey] = base64;
              updateSubmitState();
            };
            reader.readAsDataURL(file);
          }
        });
        fieldWrap.appendChild(fileInput);
        inputEls.push({ field, el: fileInput as any });
      } else {
        // Text input field
        const inputId = `sc-field-input-${fieldKey.replace(/\s+/g, '-').toLowerCase()}`;
        const hintId  = `sc-field-hint-${fieldKey.replace(/\s+/g, '-').toLowerCase()}`;
        const rule    = getFieldRule(field.label);

        const input = document.createElement("input");
        input.type = "text";
        input.id = inputId;
        input.setAttribute("aria-labelledby", labelId);
        input.setAttribute("aria-describedby", hintId);
        input.placeholder = rule?.placeholder || `Enter ${field.label}`;
        input.maxLength = rule?.maxLen ? rule.maxLen + 2 : 50;
        if (rule?.numeric) input.inputMode = "numeric";
        input.style.cssText = `
          width:100%;padding:14px 16px;border-radius:${RADIUS.md}px;
          font-size:16px;font-weight:500;
          background:${this.theme.inputBg};
          color:${this.theme.text};
          border:2px solid ${this.theme.border};
          outline:none;transition:border-color 0.2s ease;
          box-sizing:border-box;
          letter-spacing:0.5px;
        `;

        // Inline hint/error element
        const hintEl = document.createElement("div");
        hintEl.id = hintId;
        hintEl.setAttribute("aria-live", "polite");
        hintEl.style.cssText = `font-size:11px;min-height:14px;margin-top:-2px;`;
        if (rule) {
          hintEl.style.color = this.theme.textMuted;
          hintEl.textContent = rule.hint;
        }

        input.addEventListener("focus", () => {
          input.style.borderColor = this.theme.primary;
        });
        input.addEventListener("blur", () => {
          const v = input.value.trim();
          if (v) {
            const err = validateFieldValue(field.label, v);
            if (err) {
              input.style.borderColor = this.theme.error;
              hintEl.style.color = this.theme.error;
              hintEl.textContent = err;
              input.setAttribute("aria-invalid", "true");
            } else {
              input.style.borderColor = this.theme.success || this.theme.primary;
              hintEl.style.color = this.theme.textMuted;
              hintEl.textContent = rule?.hint || "";
              input.setAttribute("aria-invalid", "false");
            }
          } else {
            input.style.borderColor = this.theme.border;
          }
        });
        input.addEventListener("input", () => {
          // Reset error styling while typing
          input.style.borderColor = this.theme.primary;
          hintEl.style.color = this.theme.textMuted;
          hintEl.textContent = rule?.hint || "";
          input.removeAttribute("aria-invalid");
          this.collectedFields[fieldKey] = input.value.trim();
          updateSubmitState();
        });
        fieldWrap.appendChild(input);
        fieldWrap.appendChild(hintEl);
        inputEls.push({ field, el: input });
      }
      container.appendChild(fieldWrap);
    }

    // Error area
    const errorEl = document.createElement("div");
    errorEl.id = "sc-id-error";
    errorEl.style.cssText = `
      color:${this.theme.error};font-size:13px;display:none;
      padding:8px 12px;border-radius:${RADIUS.sm}px;background:${this.theme.errorBg};
    `;
    container.appendChild(errorEl);

    // Submit
    const submitBtn = this.createPrimaryButton("Verify & Continue");
    submitBtn.disabled = true;
    submitBtn.style.opacity = "0.5";

    const updateSubmitState = () => {
      // All inputs must have a value AND pass format validation
      const allFilled = inputEls.every(({ el }) => el.value.trim().length > 0);
      const allValid  = inputEls.every(({ field, el }) => {
        if (el.tagName === "SELECT") return el.value.trim().length > 0;
        return !validateFieldValue(field.label, el.value.trim());
      });
      const ok = allFilled && allValid;
      submitBtn.disabled = !ok;
      submitBtn.style.opacity = ok ? "1" : "0.5";
    };

    submitBtn.addEventListener("click", async () => {
      submitBtn.disabled = true;
      submitBtn.innerHTML = `<span style="display:inline-flex;align-items:center;justify-content:center;gap:8px;">${renderInlineSpinner("#fff", 13)}Verifying...</span>`;
      errorEl.style.display = "none";

      try {
        this.verificationResult = await this.sdk.onboarding.verify({
          identity_type_id: this.selectedChannelId,
          fields: { ...this.collectedFields },
        });

        if (this.verificationResult.status === "success") {
          // Save identifier and identity_check_id for liveness step
          const fieldValues = Object.values(this.collectedFields);
          this.idNumber = fieldValues[0] || "";
          this.identityCheckId = (this.verificationResult as any).data?.identity_check_id || null;

          // W3: Store name + gender from IVS result for confirmation screen
          const d = (this.verificationResult as any).data || {};
          const nd = d.normalized_data || d.data || d;
          this._ivsName = [nd.first_name || nd.firstname || "", nd.last_name || nd.lastname || nd.surname || ""]
            .map(s => String(s || "").trim()).filter(Boolean).join(" ");
          this._ivsGender = nd.gender || "";

          // W4: If IVS returned no name, there is nothing for the user to confirm —
          // auto-proceed straight to Stage 2 (liveness) instead of showing a
          // confirmation screen with no identity details to check.
          if (!this._ivsName) {
            this.showStep("liveness");
            return;
          }

          // Route to confirmation screen — user must confirm before liveness
          this.showStep("confirm_identity");
        } else {
          errorEl.textContent = _sanitizeUserMessage(this.verificationResult.message)
            || "We could not verify your details. Please check and try again.";
          errorEl.style.display = "block";
          submitBtn.textContent = "Verify & Continue";
          submitBtn.disabled = false;
          submitBtn.style.opacity = "1";
        }
      } catch (err: any) {
        // Show a single plain-language error — never raw field:message format
        const fieldErrors = err.data?.errors;
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
          // Pick the first meaningful error message — strip field prefix
          const firstMsg = fieldErrors[0]?.message || fieldErrors[0]?.detail || "";
          errorEl.textContent = _sanitizeUserMessage(firstMsg)
            || "Please check your details and try again.";
        } else if (err.errorCode === "VERIFICATION_FAILED") {
          // Prefer the backend's actual reason (e.g. "BVN check failed: name mismatch")
          // over a generic message — it tells the user what to actually fix.
          errorEl.textContent = _sanitizeUserMessage(err.message)
            || "We could not verify those details. Please check your ID number and try again.";
        } else if (err.errorCode === "CHANNEL_NOT_CONFIGURED") {
          errorEl.textContent = "This ID type is not currently available. Please choose a different option.";
        } else if (err.errorCode === "INSUFFICIENT_BALANCE") {
          errorEl.textContent = "Verification is temporarily unavailable. Please contact support.";
        } else {
          errorEl.textContent = _sanitizeUserMessage(err.message)
            || "Something went wrong. Please try again.";
        }
        errorEl.style.display = "block";
        submitBtn.textContent = "Verify & Continue";
        submitBtn.disabled = false;
        submitBtn.style.opacity = "1";
      }
    });
    container.appendChild(submitBtn);


    // Focus first input
    const firstInput = inputEls[0]?.el;
    if (firstInput && firstInput instanceof HTMLInputElement) {
      setTimeout(() => firstInput.focus(), 350);
    }
  }

  // ── Document Capture ────────────────────────────────────────────

  
  private renderDocumentCapture(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;gap:14px;";

    // Strip "with face", "(NIN)", "(BVN)" noise from the user-visible title
    const rawName = this.selectedIdTypeName || this.selectedIdType || "your document";
    const cleanDocTitle = rawName
      .replace(/\s*with\s+face\s*/gi, ' ')
      .replace(/\s*\(NIN\)\s*/gi, ' ')
      .replace(/\s*\(BVN\)\s*/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();

    const title = this.createStepTitle(`Capture ${cleanDocTitle}`);
    container.appendChild(title);

    const subtitle = document.createElement("div");
    subtitle.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin-top:-8px;`;
    subtitle.textContent = "Upload or capture your document photo. Ensure it's clear and readable.";
    container.appendChild(subtitle);

    const captureContainer = document.createElement("div");
    captureContainer.style.cssText = `
      width: 100%;
      max-width: 400px;
      margin: 0 auto;
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      background: ${this.theme.cardBg};
      border: 2px solid ${this.theme.border};
    `;
    container.appendChild(captureContainer);

    // Use the new DocumentCapture component with front/back support
    this.docCapture = new DocumentCapture();
    this.docCapture
      .capture(
        captureContainer,
        this.theme,
        this.selectedIdTypeName || this.selectedIdType,
        this.selectedRequiresBack
      )
      .then(async (result: DocumentCaptureResult) => {
        await this.processDocumentCaptureResult(result, captureContainer, container);
      })
      .catch((err: any) => {
        // Back, retake and close end the capture on purpose and already move
        // to their own screen; showing "Verification Unsuccessful" over it
        // would turn a navigation into a failed check.
        if (err instanceof DocumentCaptureCancelledError) return;
        console.error("Document capture failed:", err);
        this.showErrorStep(err.message || "Document capture failed. Please try again.");
      });
  }

  // ── W3: Identity Confirmation (data verification) ───────────────

  private renderConfirmIdentity(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:16px;padding:8px 0;";

    // W14: Check retry limit before showing screen
    if (this._confirmRetries >= SmartComplyFlow.MAX_RETRIES) {
      this._renderRetryLimitReached(container);
      return;
    }

    // W13: Pre-warm camera silently while user reads this screen
    this._prewarmCamera();

    const iconWrap = document.createElement("div");
    iconWrap.style.cssText = `width:64px;height:64px;border-radius:50%;background:${this.theme.primary}15;border:2px solid ${this.theme.primary}30;display:flex;align-items:center;justify-content:center;color:${this.theme.primary};`;
    iconWrap.innerHTML = icon("shield", 28);
    container.appendChild(iconWrap);

    const title = document.createElement("h2");
    title.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    title.textContent = this._ivsName ? `Is this you?` : "Identity Confirmed";
    container.appendChild(title);

    if (this._ivsName) {
      // Profile card
      const card = document.createElement("div");
      card.style.cssText = `width:100%;padding:16px;border-radius:14px;background:${this.theme.cardBg};border:1.5px solid ${this.theme.border};text-align:left;display:flex;flex-direction:column;gap:10px;`;

      const rows: { label: string; value: string }[] = [
        { label: "Name", value: this._ivsName },
      ];
      if (this._ivsGender) {
        const gLabel = this._ivsGender.toLowerCase();
        rows.push({ label: "Gender", value: gLabel === "m" || gLabel === "male" || gLabel === "1" ? "Male" : gLabel === "f" || gLabel === "female" || gLabel === "2" ? "Female" : this._ivsGender });
      }
      rows.push({ label: "ID Type", value: _formatDocType(this.selectedIdType) || this.selectedIdTypeName || this.selectedIdType });

      rows.forEach(({ label, value }) => {
        const row = document.createElement("div");
        row.style.cssText = "display:flex;justify-content:space-between;align-items:center;";
        const lEl = document.createElement("span");
        lEl.style.cssText = `color:${this.theme.textMuted};font-size:12px;font-weight:500;`;
        lEl.textContent = label;
        const vEl = document.createElement("span");
        vEl.style.cssText = `color:${this.theme.text};font-size:13px;font-weight:600;max-width:60%;text-align:right;`;
        vEl.textContent = value;
        row.appendChild(lEl);
        row.appendChild(vEl);
        card.appendChild(row);
      });
      container.appendChild(card);
    } else {
      const sub = document.createElement("p");
      sub.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin:0;max-width:280px;line-height:1.6;`;
      sub.textContent = "Your details have been confirmed. Please continue to the face check.";
      container.appendChild(sub);
    }

    const yesBtn = this.createPrimaryButton("Yes, that's me — continue");
    this._onceClick(yesBtn, () => {
      // B5: Reset retry counter on successful confirmation
      this._confirmRetries = 0;
      this.showStep("liveness");
    });
    container.appendChild(yesBtn);

    const noBtn = this.createSecondaryButton("No, use a different ID");
    this._onceClick(noBtn, () => {
      this._confirmRetries++;
      this._ivsName = "";
      this._ivsGender = "";
      this.identityCheckId = null;
      this.verificationResult = null;
      this.showStep("id_input");
    });
    container.appendChild(noBtn);
  }

  // ── W4: Document Confirmation (document verification) ────────────

  private renderDocumentConfirm(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;padding:8px 0;";

    // W14: Check retry limit
    if (this._confirmRetries >= SmartComplyFlow.MAX_RETRIES) {
      this._renderRetryLimitReached(container);
      return;
    }

    // OCR reported "done" but extracted nothing usable — do not let the user
    // click through to liveness with no verified identity data. This is the
    // ocr_status==="done"-with-empty-fields sibling of the poll timeout case
    // (which _renderOcrTimeout already catches in renderOcrGate).
    if (!this._ocrName && !this._ocrDocNumber && !this._ocrExpiry) {
      this._renderOcrTimeout(container);
      return;
    }

    // W13: Pre-warm camera while user reads — but only if the document photo
    // itself came from the camera. Pre-warming here when the user uploaded
    // from gallery means requesting camera access before they've touched
    // their camera at all this session, which reads as an unexplained/
    // unexpected prompt rather than a seamless continuation.
    if (this._documentViaCamera) this._prewarmCamera();

    // Step badge — pairs with DocumentCapture.showPreview's "Step 1 of 2"
    // badge shown right after capture, so this doesn't read as unexplained
    // repetition: that screen checked photo clarity, this one checks the
    // OCR-extracted data.
    const stepBadge = document.createElement("div");
    stepBadge.style.cssText = `
      display:inline-flex;align-items:center;gap:5px;
      padding:3px 11px;border-radius:${RADIUS.pill}px;
      font-size:10.5px;font-weight:600;letter-spacing:0.2px;
      color:${this.theme.primary};background:${this.theme.primary}15;border:1px solid ${this.theme.primary}30;
    `;
    stepBadge.textContent = "STEP 2 OF 2 · CHECK WHAT WE READ";
    container.appendChild(stepBadge);

    const iconWrap = document.createElement("div");
    iconWrap.style.cssText = `width:64px;height:64px;border-radius:50%;background:${this.theme.primary}15;border:2px solid ${this.theme.primary}30;display:flex;align-items:center;justify-content:center;color:${this.theme.primary};`;
    iconWrap.innerHTML = icon("document", 28);
    container.appendChild(iconWrap);

    const title = document.createElement("h2");
    title.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    title.textContent = "Are these your details?";
    container.appendChild(title);

    const sub = document.createElement("p");
    sub.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin:0;max-width:280px;line-height:1.5;`;
    sub.textContent = "We read the following from your document. Please confirm they are correct before continuing.";
    container.appendChild(sub);

    // Document face thumbnail if available
    if (this._ocrFaceImageUrl) {
      const faceWrap = document.createElement("div");
      faceWrap.style.cssText = `width:80px;height:80px;border-radius:50%;overflow:hidden;border:3px solid ${this.theme.primary};flex-shrink:0;margin:0 auto;`;
      const faceImg = document.createElement("img");
      faceImg.src = this._ocrFaceImageUrl;
      faceImg.alt = "Photo from your document";
      faceImg.style.cssText = "width:100%;height:100%;object-fit:cover;";
      faceWrap.appendChild(faceImg);
      container.appendChild(faceWrap);
    }

    // Expired or expiring-soon notice
    if (this._ocrIsExpired) {
      const expired = document.createElement("div");
      expired.setAttribute("role", "alert");
      expired.style.cssText = `width:100%;padding:10px 14px;border-radius:10px;background:${this.theme.errorBg};border:1px solid ${this.theme.error}40;color:${this.theme.error};font-size:12px;text-align:left;line-height:1.5;`;
      expired.textContent = "⚠️  This document has expired. You can continue, but a check with an expired document won't be approved. Use a valid document if you have one.";
      container.appendChild(expired);
    } else if (this._ocrExpirySoon) {
      const warn = document.createElement("div");
      warn.style.cssText = `width:100%;padding:10px 14px;border-radius:10px;background:${this.theme.warning}18;border:1px solid ${this.theme.warning}40;color:${this.theme.warning};font-size:12px;text-align:left;line-height:1.5;`;
      warn.textContent = "⚠️  Your document expires soon. Some services may not accept it, but you can still continue.";
      container.appendChild(warn);
    }

    // Details card — show whatever OCR returned, never block on missing fields
    const card = document.createElement("div");
    card.style.cssText = `width:100%;padding:14px 16px;border-radius:14px;background:${this.theme.cardBg};border:1.5px solid ${this.theme.border};text-align:left;display:flex;flex-direction:column;gap:10px;`;

    const docRows: { label: string; value: string }[] = [];
    if (this._ocrName)      docRows.push({ label: "Name",       value: this._ocrName });
    if (this._ocrDocNumber) docRows.push({ label: "Document No", value: this._ocrDocNumber });
    if (this._ocrExpiry)    docRows.push({ label: "Expires",     value: this._ocrExpiry });
    docRows.push({ label: "Document Type", value: _formatDocType(this.selectedIdType) || this.selectedIdTypeName });

    if (docRows.length === 0) {
      const fallback = document.createElement("div");
      fallback.style.cssText = `color:${this.theme.textSecondary};font-size:12px;`;
      fallback.textContent = "Document scanned successfully.";
      card.appendChild(fallback);
    } else {
      docRows.forEach(({ label, value }) => {
        if (!value) return;
        const row = document.createElement("div");
        row.style.cssText = "display:flex;justify-content:space-between;align-items:center;";
        const lEl = document.createElement("span");
        lEl.style.cssText = `color:${this.theme.textMuted};font-size:12px;font-weight:500;`;
        lEl.textContent = label;
        const vEl = document.createElement("span");
        vEl.style.cssText = `color:${this.theme.text};font-size:13px;font-weight:600;max-width:60%;text-align:right;`;
        vEl.textContent = value;
        row.appendChild(lEl);
        row.appendChild(vEl);
        card.appendChild(row);
      });
    }
    container.appendChild(card);

    const yesBtn = this.createPrimaryButton("Yes, continue");
    this._onceClick(yesBtn, () => {
      // B5: Reset retry counter on successful confirmation
      this._confirmRetries = 0;
      this.showStep("liveness");
    });
    container.appendChild(yesBtn);

    const noBtn = this.createSecondaryButton("No, retake document");
    this._onceClick(noBtn, () => {
      // Destroy the old DocumentCapture instance before replacing it —
      // matches the header back-button handler for this same step (see
      // backTargets.document_confirm) so retaking via either path releases
      // any still-open camera stream the same way, instead of silently
      // overwriting this.docCapture without cleanup.
      this.docCapture?.destroy();
      this._confirmRetries++;
      this._ocrName = ""; this._ocrDocNumber = ""; this._ocrExpiry = "";
      this._ocrFaceImageUrl = ""; this._ocrIsExpired = false; this._ocrExpirySoon = false;
      this.livenessEntryId = null;
      this.documentBlob = null; this.documentBackBlob = null;
      this.showStep("document_capture");
    });
    container.appendChild(noBtn);
  }

  // ── W6+W7+W8: OCR Gate (document verification) ───────────────────

  private renderOcrGate(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;gap:14px;padding:20px 0;text-align:center;";

    container.appendChild(renderSpinner(this.theme, { size: 52, icon: icon("document", 20) }));

    const statusEl = document.createElement("div");
    statusEl.setAttribute("aria-live", "polite");
    statusEl.style.cssText = `color:${this.theme.text};font-size:${TYPE.base}px;font-weight:600;`;
    statusEl.textContent = "Reading your document…";
    container.appendChild(statusEl);

    const subEl = document.createElement("div");
    subEl.style.cssText = `color:${this.theme.textSecondary};font-size:12px;max-width:260px;line-height:1.5;`;
    subEl.textContent = "This usually takes 5–15 seconds. Please wait.";
    container.appendChild(subEl);

    if (!this.livenessEntryId) {
      this.showErrorStep("Document upload failed. Please try again.");
      return;
    }

    const entryId = this.livenessEntryId;
    // Backend OCR (process_document_ocr) now submits + polls SmartComply
    // synchronously in one Celery task, no webhook — up to ~40s per side
    // (MAX_POLL_ATTEMPTS x POLL_INTERVAL_SECONDS in smartcomply_ocr.py),
    // so a two-sided document (front + back) can take up to ~80s end to
    // end. 45 x 2s = 90s comfortably covers that worst case while the loop
    // still exits immediately once ocrDone is true — the common case
    // (OCR done in a few seconds) is exactly as fast as before.
    const MAX_POLLS = 45; // 45 x 2s = 90s timeout
    let polls = 0;

    const poll = async () => {
      if (this.isDestroyed) return;
      polls++;
      try {
        const result = await this.sdk.liveness.pollResult(entryId) as any;

        // An expired document does not stop the flow: liveness and the face
        // match still run so the client gets the full result, and the backend
        // fails the check at the end with "Document has expired." The confirm
        // screen tells the user (see _ocrIsExpired).

        // W8: OCR quality fail — offer retake
        if (result.ocr_quality_failed === true) {
          container.innerHTML = "";
          this._renderOcrQualityFail(container, result.ocr_rejection_reason);
          return;
        }

        // The backend can fail this entry for a reason that ISN'T expiry or
        // OCR quality — specifically, the IPRS/government-registry re-check
        // (the ID number read off the document doesn't exist, or belongs to
        // someone else). By this point in the poll, ocr_status/ocr_name/
        // ocr_document_number are already populated from the OCR step that
        // ran before the registry check, so the ocrDone check below would
        // otherwise treat this as a success and route the user straight to
        // document_confirm on an entry that is already dead. Must be checked
        // before ocrDone, not after.
        if (result.status === "failed") {
          container.innerHTML = "";
          this._renderDocumentVerificationFailed(container, result.failure_reason);
          return;
        }

        // OCR complete — store data and show confirmation screen
        const ocrDone = result.ocr_status === "done" || result.ocr_status === "ocr_complete"
          || result.ocr_name || result.ocr_document_number || result.ocr_expiry_date;

        if (ocrDone) {
          this._ocrName          = result.ocr_name || "";
          this._ocrDocNumber     = result.ocr_document_number || "";
          this._ocrExpiry        = result.ocr_expiry_date || "";
          this._ocrFaceImageUrl  = result.ocr_face_image_url || "";
          this._ocrIsExpired     = result.is_expired === true;

          // B8: Normalise expiry date string before parsing — OCR may return
          // DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, or "12 May 2029" etc.
          // new Date() alone is locale-dependent on non-ISO strings.
          if (this._ocrExpiry) {
            const expDate = _parseOcrDate(this._ocrExpiry);
            if (expDate) {
              const sixMonths = new Date();
              sixMonths.setMonth(sixMonths.getMonth() + 6);
              this._ocrExpirySoon = expDate <= sixMonths && expDate > new Date();
            }
          }

          this.showStep("document_confirm");
          return;
        }

        // Still pending — keep polling. Past ~20s (the old timeout window)
        // update the message so a long-but-healthy OCR pass (front+back can
        // take up to ~80s server-side) doesn't look frozen to the user.
        if (polls === 10 && !this.isDestroyed) {
          statusEl.textContent = "Still reading your document…";
          subEl.textContent = "Documents with a back side can take a little longer. Please keep waiting.";
        }
        if (polls < MAX_POLLS && !this.isDestroyed) {
          this._ocrPollTimer = setTimeout(poll, 2000);
        } else if (!this.isDestroyed) {
          // Genuinely exhausted the whole window with nothing usable read —
          // stop here instead of letting an unverified identity through to
          // liveness. document_confirm never blocks on missing fields (by
          // design, so a partial-but-real OCR read still lets the user
          // continue), so the gate has to live here, before that screen.
          container.innerHTML = "";
          this._renderOcrTimeout(container);
        }
      } catch {
        if (polls < MAX_POLLS && !this.isDestroyed) {
          this._ocrPollTimer = setTimeout(poll, 3000);
        } else if (!this.isDestroyed) {
          this.showErrorStep("We could not read your document. Please check your connection and try again.");
        }
      }
    };

    // First poll after 3s — give OCR submission time to start
    this._ocrPollTimer = setTimeout(poll, 3000);
  }

  private _renderOcrQualityFail(container: HTMLElement, reason?: string): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;padding:20px 0;";
    const icon = document.createElement("div");
    icon.style.cssText = `width:72px;height:72px;border-radius:50%;background:${this.theme.errorBg};border:3px solid ${this.theme.error};display:flex;align-items:center;justify-content:center;font-size:28px;`;
    icon.textContent = "🔍";
    container.appendChild(icon);
    const t = document.createElement("h2");
    t.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    // "Photo unclear" only fits the blur/lighting reasons below — a wrong
    // document entirely (mismatch/no-document-detected) isn't a clarity
    // problem, so give it its own, accurate title instead of implying the
    // user just needs better lighting.
    const isWrongDocument = reason === "document_not_detected"
      || reason === "document_type_mismatch"
      || reason === "country_mismatch";
    t.textContent = isWrongDocument ? "That Doesn't Look Right" : "Document Photo Unclear";
    container.appendChild(t);
    const msg = reason === "image_too_blurry" ? "Your document photo is too blurry. Please retake it in good lighting with a steady hand."
      : reason === "image_too_dark" ? "Your document photo is too dark. Please retake it in better lighting."
      : reason === "image_too_bright" ? "Your document photo has too much glare. Please reduce reflections and retake."
      : reason === "document_not_detected" ? "We could not detect a document in the photo. Please make sure you're photographing the actual ID document, with the full document visible."
      : reason === "document_type_mismatch" ? "This document doesn't match the ID type you selected. Please upload the correct document, or go back and choose the matching type."
      : reason === "country_mismatch" ? "This document doesn't appear to be from the expected country. Please check you've selected the right country and document."
      // Any other reason: if it already reads like a real sentence (starts
      // capitalized, has spaces — matches the backend's own
      // _safe_failure_reason heuristic for "already user-facing"), show it
      // as-is instead of masking a specific, accurate reason behind a
      // generic "bad lighting" message that may not describe what's wrong.
      : reason && /^[A-Z]/.test(reason) && reason.includes(" ") ? reason
      : "We could not read your document clearly. Please retake the photo in good lighting with no glare.";
    const d = document.createElement("p");
    d.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin:0;max-width:280px;line-height:1.6;`;
    d.textContent = msg;
    container.appendChild(d);
    const retryBtn = this.createPrimaryButton("Retake photo");
    retryBtn.addEventListener("click", () => {
      this.livenessEntryId = null; this.documentBlob = null; this.documentBackBlob = null;
      this.showStep("document_capture");
    });
    container.appendChild(retryBtn);
  }

  /**
   * The document passed OCR's own quality gate (it was readable) but failed
   * the government-registry re-check — the ID number either doesn't exist
   * in the registry, or belongs to a different person than the name on the
   * document. Distinct from _renderOcrQualityFail: the photo itself may be
   * perfectly clear, so "retake photo" framing would be misleading — the
   * fix is a correct, uncropped photo of the actual document, not just a
   * clearer one.
   */
  private _renderDocumentVerificationFailed(container: HTMLElement, failureReason?: string | null): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;padding:20px 0;";
    const icon = document.createElement("div");
    icon.style.cssText = `width:72px;height:72px;border-radius:50%;background:${this.theme.errorBg};border:3px solid ${this.theme.error};display:flex;align-items:center;justify-content:center;font-size:28px;`;
    icon.textContent = "⚠️";
    container.appendChild(icon);
    const t = document.createElement("h2");
    t.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    t.textContent = "We Couldn't Confirm Your Details";
    container.appendChild(t);
    const d = document.createElement("p");
    d.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin:0;max-width:280px;line-height:1.6;`;
    // failure_reason is already sanitized server-side (_safe_failure_reason)
    // before it reaches this endpoint — safe to show directly, same as every
    // other failure_reason display in this file.
    d.textContent = failureReason
      || "We couldn't confirm your details. Please re-upload a clear photo of your ID and make sure your details are correct before submitting.";
    container.appendChild(d);
    const retryBtn = this.createPrimaryButton("Re-upload ID");
    retryBtn.addEventListener("click", () => {
      this.livenessEntryId = null; this.documentBlob = null; this.documentBackBlob = null;
      this.showStep("document_capture");
    });
    container.appendChild(retryBtn);
    const endBtn = this.createSecondaryButton("End session");
    endBtn.addEventListener("click", () => { this.options.onError?.(new Error("Document verification failed")); this.close(); });
    container.appendChild(endBtn);
  }

  /**
   * OCR ran to completion but never produced a single usable field within
   * the poll window — do not let the flow reach liveness with no verified
   * identity data. Distinct from _renderOcrQualityFail (a bad photo) since
   * this covers a genuinely stuck/slow OCR pass instead of a rejected image.
   */
  private _renderOcrTimeout(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;padding:20px 0;";
    const icon = document.createElement("div");
    icon.style.cssText = `width:72px;height:72px;border-radius:50%;background:${this.theme.errorBg};border:3px solid ${this.theme.error};display:flex;align-items:center;justify-content:center;font-size:28px;`;
    icon.textContent = "⏱️";
    container.appendChild(icon);
    const t = document.createElement("h2");
    t.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    t.textContent = "Couldn't Read Your Document";
    container.appendChild(t);
    const d = document.createElement("p");
    d.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin:0;max-width:280px;line-height:1.6;`;
    d.textContent = "We weren't able to read any details from your document. Please retake the photo in good lighting, with all four corners visible.";
    container.appendChild(d);
    const retryBtn = this.createPrimaryButton("Retake photo");
    retryBtn.addEventListener("click", () => {
      this.livenessEntryId = null; this.documentBlob = null; this.documentBackBlob = null;
      this.showStep("document_capture");
    });
    container.appendChild(retryBtn);
  }

  // ── W11: You're all done (end-user final screen) ─────────────────

  private renderDone(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:16px;padding:24px 0;";
    container.setAttribute("role", "status");

    // B7: Fire onComplete immediately when the done screen renders — the end
    // user has completed their part regardless of whether they click Close.
    // Firing here means the integrator always gets the event even if the user
    // closes the modal via the ✕ button instead of the Close button.
    this.options.onComplete?.({
      entryId: this.livenessEntryId || 0,
      sessionId: this.sdk.sessionToken,
      status: "processing",
      submittedAt: new Date().toISOString(),
      verificationResult: this.verificationResult || undefined,
    });

    const iconWrap = document.createElement("div");
    iconWrap.style.cssText = `width:80px;height:80px;border-radius:50%;background:${this.theme.successBg};border:3px solid ${this.theme.success};display:flex;align-items:center;justify-content:center;color:${this.theme.success};animation:sc-pop 0.4s ease;`;
    iconWrap.innerHTML = icon("check", 36);
    container.appendChild(iconWrap);

    const title = document.createElement("h2");
    title.style.cssText = `color:${this.theme.text};font-size:${TYPE.headingLg}px;font-weight:700;margin:0;`;
    title.textContent = "You're all done!";
    container.appendChild(title);

    const desc = document.createElement("p");
    desc.style.cssText = `color:${this.theme.textSecondary};font-size:14px;line-height:1.6;margin:0;max-width:300px;`;
    desc.textContent = "Your verification is complete.";
    container.appendChild(desc);

    // Subtle info line
    const info = document.createElement("div");
    info.style.cssText = `color:${this.theme.textMuted};font-size:11px;display:flex;align-items:center;gap:4px;`;
    info.textContent = "🔒  Your data is encrypted and secure";
    container.appendChild(info);

    const doneBtn = this.createPrimaryButton("Close");
    doneBtn.addEventListener("click", () => {
      // onComplete already fired on render — just close the modal
      this.close();
    });
    container.appendChild(doneBtn);
  }

  // ── W14: Retry limit reached ─────────────────────────────────────

  private _renderRetryLimitReached(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;padding:24px 0;";
    const icon = document.createElement("div");
    icon.style.cssText = `width:72px;height:72px;border-radius:50%;background:${this.theme.errorBg};border:3px solid ${this.theme.error};display:flex;align-items:center;justify-content:center;font-size:28px;`;
    icon.textContent = "⚠️";
    container.appendChild(icon);
    const t = document.createElement("h2");
    t.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    t.textContent = "Unable to Complete Verification";
    container.appendChild(t);
    const d = document.createElement("p");
    d.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin:0;max-width:280px;line-height:1.6;`;
    d.textContent = "We were unable to complete your verification at this time. Please contact support or try again later.";
    container.appendChild(d);
    const closeBtn = this.createPrimaryButton("Close");
    closeBtn.addEventListener("click", () => { this.options.onError?.(new Error("Retry limit reached")); this.close(); });
    container.appendChild(closeBtn);
  }

  // ── W13: Camera pre-warm ─────────────────────────────────────────

  private _prewarmCamera(): void {
    if (this._prewarmedStream || this.isDestroyed) return;
    navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: "user" } })
      .then(stream => {
        if (this.isDestroyed) { stream.getTracks().forEach(t => t.stop()); return; }
        this._prewarmedStream = stream;
      })
      .catch(() => { /* silent — pre-warm is best effort */ });
  }

  /**
   * Process document capture result with front/back support
   */
  private async processDocumentCaptureResult(
    result: DocumentCaptureResult,
    captureContainer: HTMLElement,
    container: HTMLElement
  ): Promise<void> {
    // Uploaded exactly as captured or picked, photo or PDF: re-encoding a
    // small passport photo as JPEG blurred its machine-readable lines and the
    // OCR rejected a clear Rwandan passport as "country: unknown".
    this.documentBlob = result.front;
    this.documentBackBlob = result.back || null;
    this._documentViaCamera = result.viaCamera;

    // Process the front image — pass the already-compressed blob
    await this.processDocumentBlob(this.documentBlob, captureContainer, container);
    }

  /**
   * Process document blob - common method for both CameraView and DocumentCapture
   */
  private async processDocumentBlob(
    blob: Blob,
    captureContainer: HTMLElement,
    container: HTMLElement
  ): Promise<void> {
    const maxRetries = 2;
    let retryCount = 0;

    const attemptProcessing = async (): Promise<void> => {
      try {
        if (!blob || blob.size === 0) {
          throw new Error("Failed to capture document. Please try again.");
        }
        if (blob.size > MAX_DOCUMENT_BYTES) {
          throw new Error(
            `Document is too large. Please use a file smaller than ${Math.round(MAX_DOCUMENT_BYTES / (1024 * 1024))}MB.`
          );
        }

        this.documentBlob = blob;

    // Show scanning state
    captureContainer.innerHTML = "";
    captureContainer.style.cssText += "display:flex;flex-direction:column;align-items:center;gap:14px;padding:28px 20px;";
    captureContainer.appendChild(renderSpinner(this.theme, { size: 52, icon: icon("search", 20) }));
    captureContainer.insertAdjacentHTML("beforeend", `
        <div style="color:${this.theme.text};font-size:14px;font-weight:600;">Scanning document...</div>
        <div style="color:${this.theme.textSecondary};font-size:12px;text-align:center;max-width:200px;line-height:1.5;">Running OCR and verifying your identity</div>`);

      // Create liveness entry with document.
      // Use the first collected field value as identifier when available
      // (document flow has no data-verification step, so collectedFields is
      // empty — fall back to the selected ID type code so the webhook
      // subject.identifier is at least human-meaningful, not a raw timestamp).
      const identifier = Object.values(this.collectedFields)[0]
        || this.idNumber
        || this.selectedIdType
        || `DOC-${Date.now()}`;
      const country = countryCodeFor(this.sdkConfig?.channels || {}, this.selectedCountry);

      const deviceMeta = collectDeviceMetadata();
      let createResult;
      try {
        createResult = await this.sdk.liveness.create({
          identifier,
          identifier_type: this.selectedIdType,
          country,
          // Was hardcoded to ["BLINK"] only. This entry is reused for the
          // liveness step later via existingEntryId (see liveness.ts's
          // startCheck) — that reuse path builds a LOCAL stub object and
          // never calls create() again, so whatever challenge_actions is
          // sent HERE is what the backend permanently stores for this
          // entry, regardless of what the widget actually challenges the
          // user for afterward. A document-flow entry's liveness_challenges
          // was stuck showing only BLINK even when the customer genuinely
          // completed a full blink-and-motion scan, because the stored
          // set never included TURN_HEAD to begin with. Match
          // DEFAULT_CHALLENGE_ACTIONS so this is consistent with what the
          // liveness step (SmartComplyFlow's other startCheck call) and
          // the widget's own ActionDetector actually check for.
          challenge_actions: [...DEFAULT_CHALLENGE_ACTIONS],
          document: blob,
          document_back: this.documentBackBlob || undefined,
          device_type: deviceMeta.device_type,
          os_name: deviceMeta.os_name,
          browser_timezone: deviceMeta.browser_timezone,
          fingerprint_id: deviceMeta.fingerprint_id,
        });
      } catch (apiError: any) {
        // Handle specific network and API errors
        if (apiError.message?.includes('fetch') || apiError.message?.includes('network') || apiError.code === 'NETWORK_ERROR') {
          throw new Error("Network connection failed. Please check your internet connection and try again.");
        } else if (apiError.message?.includes('413') || apiError.message?.includes('too large')) {
          throw new Error("Document image is too large. Please use a smaller image or retake the photo.");
        } else if (apiError.message?.includes('415') || apiError.message?.includes('format')) {
          throw new Error("Unsupported image format. Please use JPEG or PNG images.");
        } else if (apiError.message?.includes('401') || apiError.message?.includes('unauthorized')) {
          throw new Error("Session expired. Please refresh the page and try again.");
        } else if (apiError.message?.includes('429') || apiError.message?.includes('rate limit')) {
          throw new Error("Too many requests. Please wait a moment and try again.");
        } else {
          throw new Error(apiError.message || "Failed to process document. Please try again.");
        }
      }

      this.livenessEntryId = createResult.id;

      // W6: Route to OCR gate — poll until OCR result is ready, validate
      // expiry/quality, then show document_confirm before liveness starts.
      this.showStep("ocr_gate");

      } catch (err: any) {
        console.error("Document processing failed:", err);
        
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying document processing (attempt ${retryCount}/${maxRetries})`);
          
          // Show retry message
          if (captureContainer) {
            captureContainer.innerHTML = "";
            captureContainer.style.cssText += "display:flex;flex-direction:column;align-items:center;gap:16px;padding:28px 20px;text-align:center;";
            captureContainer.appendChild(renderSpinner(this.theme, { size: 52, icon: icon("refresh", 20) }));
            captureContainer.insertAdjacentHTML("beforeend", `
                <div style="color:${this.theme.text};font-size:14px;font-weight:600;">Retrying...</div>
                <div style="color:${this.theme.textSecondary};font-size:12px;max-width:240px;line-height:1.5;">Attempt ${retryCount} of ${maxRetries}</div>`);
          }
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 2000));
          return attemptProcessing();
        } else {
          // Final retry failed, show detailed error
          this.handleDocumentProcessingError(err, captureContainer);
        }
      }
    };

    await attemptProcessing();
  }

  private handleDocumentProcessingError(error: any, captureContainer: HTMLElement): void {
    // Map technical errors to user-friendly messages — never pass raw error strings
    let userMessage = "We could not verify your document. Please try again.";
    let canRetry = true;

    const raw = (error?.message || "").toLowerCase();
    if (raw.includes("expired")) {
      userMessage = "The document you submitted has expired. Please use a valid, current ID.";
      canRetry = false;
    } else if (raw.includes("quality too low") || raw.includes("blurry") || raw.includes("blur")) {
      userMessage = "The document photo isn't clear enough. Please retake it in good lighting with no glare.";
    } else if (raw.includes("not detected") || raw.includes("document_not_detected")) {
      userMessage = "We could not detect a document in the photo. Please ensure the full ID is visible and try again.";
    } else if (raw.includes("network") || raw.includes("fetch") || raw.includes("connection")) {
      userMessage = "A connection error occurred. Please check your internet and try again.";
    } else if (raw.includes("too large") || raw.includes("413")) {
      userMessage = "The document image is too large. Please retake the photo.";
    } else if (raw.includes("format") || raw.includes("415")) {
      userMessage = "Unsupported image format. Please use JPEG or PNG.";
    } else if (raw.includes("type mismatch") || raw.includes("document_type_mismatch")) {
      userMessage = "The document does not match the selected ID type. Please choose the correct ID type.";
    }

    if (captureContainer) {
      // Build with DOM — never interpolate user-derived content into innerHTML
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;flex-direction:column;align-items:center;gap:16px;padding:28px 20px;text-align:center;";
      wrap.setAttribute("role", "alert");

      const icon = document.createElement("div");
      icon.style.cssText = `width:56px;height:56px;border-radius:50%;background:${this.theme.errorBg};border:3px solid ${this.theme.error};display:flex;align-items:center;justify-content:center;font-size:26px;`;
      icon.textContent = "🔍";
      wrap.appendChild(icon);

      const titleEl = document.createElement("div");
      titleEl.style.cssText = `color:${this.theme.text};font-size:14px;font-weight:700;`;
      titleEl.textContent = "Document Could Not Be Verified";
      wrap.appendChild(titleEl);

      const msgEl = document.createElement("div");
      msgEl.style.cssText = `color:${this.theme.textSecondary};font-size:13px;max-width:260px;line-height:1.6;`;
      msgEl.textContent = userMessage;
      wrap.appendChild(msgEl);

      if (canRetry) {
        const retryBtn = document.createElement("button");
        retryBtn.textContent = "Try Again";
        retryBtn.setAttribute("aria-label", "Retake document photo");
        retryBtn.style.cssText = `margin-top:8px;padding:12px 24px;border-radius:${RADIUS.sm}px;background:${this.theme.primary};color:#fff;border:none;font-size:14px;font-weight:600;cursor:pointer;`;
        retryBtn.addEventListener("click", () => this.showStep("document_capture"));
        wrap.appendChild(retryBtn);
      }

      captureContainer.innerHTML = "";
      captureContainer.appendChild(wrap);
    } else {
      this.showErrorStep(userMessage);
    }
  }

  // ── Liveness ────────────────────────────────────────────────────

  private renderLiveness(container: HTMLElement): void {
    container.style.cssText += "display:flex;flex-direction:column;gap:14px;";

    // Consumed unconditionally (even on the retry-limit-reached exit below)
    // so it never leaks into a later, unrelated render of this step.
    const restartedAfterBackground = this._restartedAfterBackground;
    this._restartedAfterBackground = false;

    // W13: Check retry limit before starting another liveness attempt
    if (this._livenessFailures >= SmartComplyFlow.MAX_RETRIES) {
      this._renderRetryLimitReached(container);
      return;
    }

    // Each render gets its own id so a stale attempt (e.g. abandoned after
    // the tab was backgrounded and we restarted the scan on return) can't
    // resolve/reject into the current one — see the visibilitychange
    // handler in mount(), which re-renders this step on return-to-foreground.
    const attemptId = ++this._livenessAttemptId;

    // Tell the user why the scan restarted instead of silently swapping
    // back into a fresh camera view with no explanation — a backgrounded
    // tab/app can leave the camera and detection loop in a stale state
    // (see the visibilitychange handler), so this restart is intentional,
    // but it must not look like the scan "just resumed" on its own.
    if (restartedAfterBackground) {
      const notice = document.createElement("div");
      notice.setAttribute("role", "status");
      notice.style.cssText = `width:100%;padding:10px 14px;border-radius:10px;background:${this.theme.warning}18;border:1px solid ${this.theme.warning}40;color:${this.theme.warning};font-size:12px;text-align:left;line-height:1.5;`;
      notice.textContent = "Your scan was interrupted when you left the page. Starting over — please stay on this screen until it's done.";
      container.appendChild(notice);
    }

    const title = this.createStepTitle("Face verification");
    container.appendChild(title);

    const desc = document.createElement("div");
    desc.style.cssText = `color:${this.theme.textSecondary};font-size:13px;margin-top:-8px;line-height:1.5;`;
    desc.textContent = "Fit your face inside the oval, then blink naturally while looking around for a few seconds.";
    container.appendChild(desc);

    // Liveness mount point
    const livenessContainer = document.createElement("div");
    livenessContainer.style.cssText = "margin:8px 0;width:100%;";
    container.appendChild(livenessContainer);

    // W9: For data verification, identity is already held by the backend via
    // identity_check_id — the real ID number/type must not be re-sent, and the
    // backend now accepts liveness/create without them when identity_check is
    // set (see liveness.ts create(), which omits both fields entirely in that
    // case). Only build a real identifier for the non-data-verification path.
    const isDataVerification = !!this.identityCheckId;
    const identifier = isDataVerification
      ? ""
      : (this.idNumber || Object.values(this.collectedFields)[0] || this.selectedIdType || `DOC-${Date.now()}`);
    const identifierType = isDataVerification ? "" : this.selectedIdType;

    const country = countryCodeFor(this.sdkConfig?.channels || {}, this.selectedCountry);

    // Start liveness check (this handles camera, detection, recording, submission)
    // If livenessEntryId exists (document flow), reuse it instead of creating a new one
    this.sdk.liveness
      .startCheck(
        livenessContainer,
        {
          identifier,
          identifier_type: identifierType,
          country,
          document: this.documentBlob || undefined,
          document_back: this.documentBackBlob || undefined,
          identity_check: this.identityCheckId || undefined,
          verification_type: this.identityCheckId
            ? "data_verification"
            : this.documentBlob
              ? "document_verification"
              : "liveness_only",
          // B4: pass pre-warmed stream so camera.open() is skipped
          prewarmedStream: this._prewarmedStream || undefined,
          colors: {
            primary: this.theme.primary,
            success: this.theme.success,
            error: this.theme.error,
            warning: this.theme.warning,
          },
        },
        [...DEFAULT_CHALLENGE_ACTIONS] as ChallengeAction[],
        this.livenessEntryId || undefined
      )
      .then((result) => {
        // A stale attempt (superseded by a restart after the tab was
        // backgrounded) resolving late must not act on the current screen.
        if (attemptId !== this._livenessAttemptId) return;
        // W11: After submit, end user is done — show "You're all done" screen.
        // Backend processing continues in background; client receives webhook.
        // Track liveness failures for retry limit (W14).
        if (result.status === "failed") {
          this._livenessFailures++;
        }
        if (result.id) {
          this.livenessEntryId = result.id;
        }
        this.showStep("done");
      })
      .catch((err) => {
        if (attemptId !== this._livenessAttemptId) return;
        // Only count a genuine verification failure against the retry limit —
        // a dropped camera, network blip, or upload failure isn't the user's
        // fault and shouldn't cost them one of their limited attempts. These
        // are infrastructure-layer errors (thrown/rejected); a real face-
        // match/quality failure comes back as a resolved result with
        // status "failed" instead (handled in .then() above).
        if (!_isInfrastructureError(err)) {
          this._livenessFailures++;
        }
        this.showErrorStep(_userFacingLivenessError(err));
      });
  }

  // ── Error ───────────────────────────────────────────────────────

  private showErrorStep(message: string): void {
    this.currentStep = "error";
    if (!this.contentArea) return;
    this.contentArea.innerHTML = "";

    const wrapper = document.createElement("div");
    wrapper.setAttribute("role", "alert");
    wrapper.style.cssText = `
      display:flex;flex-direction:column;align-items:center;
      text-align:center;gap:16px;padding:20px 0;
    `;

    // Classify the error into a user-friendly category
    const isCameraErr    = /camera|permission|notallowed|notfound/i.test(message);
    const isNetworkErr   = /network|timeout|connection|fetch/i.test(message);
    // Checked before the generic isAuthErr below — a resume/resubmit link
    // (RESUME_TARGET_UNAVAILABLE / NOT_A_RESUME_TOKEN from the backend, see
    // _resolveResumeStep) also matches "token...expired"/"link" wording, but
    // the corrective action is different from a plain session timeout: the
    // customer can't just "start again" themselves, they need a fresh link
    // from whoever sent them this one.
    const isResumeLinkErr = isResumeLinkErrorMessage(message);
    const isAuthErr     = !isResumeLinkErr && /session.*expired|token.*expired|authentication/i.test(message);
    const isDocQuality  = /blurry|blur|quality|glare|dark|bright|lighting/i.test(message);
    const isExpiredDoc  = /document.*expired|expired.*document/i.test(message);
    const isFaceMatch   = /face.*match|selfie.*match|does not match/i.test(message);
    // Billing/wallet failures (e.g. WALLET_NOT_FOUND, INSUFFICIENT_BALANCE from
    // /v1/sdk/liveness/submit/) are an operator-facing problem, not something
    // the end user can act on — never show "wallet"/"balance" language to them.
    const isBillingErr  = /wallet|balance|insufficient.*fund|payment.*required/i.test(message);

    const errorIconName: IconName = isCameraErr  ? "camera"
      : isNetworkErr  ? "network"
      : isResumeLinkErr ? "clock"
      : isAuthErr     ? "clock"
      : isDocQuality  ? "search"
      : isExpiredDoc  ? "calendar"
      : isFaceMatch   ? "face"
      : isBillingErr  ? "warning"
      : "close";

    const errorTitle = isCameraErr  ? "Camera Access Required"
      : isNetworkErr  ? "Connection Problem"
      : isResumeLinkErr ? "Link No Longer Valid"
      : isAuthErr     ? "Session Expired"
      : isDocQuality  ? "Document Photo Unclear"
      : isExpiredDoc  ? "Document Expired"
      : isFaceMatch   ? "Face Match Failed"
      : isBillingErr  ? "Verification Unavailable"
      : "Verification Unsuccessful";

    // Map to user-facing guidance — never show raw internal message
    const userMessage = isCameraErr
      ? "Please allow camera access in your browser settings and try again."
      : isNetworkErr
        ? "Check your internet connection and try again."
        : isResumeLinkErr
          ? "This link has expired or has already been used. Please contact us to request a new one."
          : isAuthErr
          ? "Your session has expired. Please start again."
          : isDocQuality
            ? _sanitizeUserMessage(message) || "Please retake your document photo in good lighting with no glare."
            : isExpiredDoc
              ? "The document you submitted has expired. Please use a valid, current ID."
              : isFaceMatch
                ? "Your selfie did not match the photo on your ID. Please ensure you are using your own valid document and face the camera directly."
                : isBillingErr
                  ? "Verification is temporarily unavailable. Please contact support."
                  : _sanitizeUserMessage(message) || "We were unable to complete your verification. Please try again.";

    const iconWrap = document.createElement("div");
    iconWrap.style.cssText = `
      width:72px;height:72px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      color:${this.theme.error};
      background:${this.theme.errorBg};
      border:3px solid ${this.theme.error};
    `;
    iconWrap.innerHTML = icon(errorIconName, 28);
    wrapper.appendChild(iconWrap);

    const title = document.createElement("h2");
    title.style.cssText = `color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;margin:0;`;
    title.textContent = errorTitle;
    wrapper.appendChild(title);

    const desc = document.createElement("p");
    desc.style.cssText = `color:${this.theme.textSecondary};font-size:14px;line-height:1.6;margin:0;max-width:300px;`;
    desc.textContent = userMessage;
    wrapper.appendChild(desc);

    const retryBtn = this.createPrimaryButton("Try Again");
    retryBtn.setAttribute("aria-label", "Try verification again");
    retryBtn.addEventListener("click", () => {
      this.initialize();
    });
    wrapper.appendChild(retryBtn);

    const closeBtn = this.createSecondaryButton("Close");
    closeBtn.setAttribute("aria-label", "Close verification widget");
    closeBtn.addEventListener("click", () => {
      this.options.onError?.(new Error(message));
      this.close();
    });
    wrapper.appendChild(closeBtn);

    this.contentArea.appendChild(wrapper);
  }

  // ─────────────────────────────────────────────────────────────────
  // UI Helpers
  // ─────────────────────────────────────────────────────────────────

  private createHeader(): HTMLElement {
    const header = document.createElement("div");
    header.setAttribute("role", "banner");
    header.style.cssText = `
      display:flex;align-items:center;justify-content:space-between;
      padding:11px 20px;border-bottom:1px solid ${this.theme.border};
    `;

    // Left: back button + brand
    const leftArea = document.createElement("div");
    leftArea.style.cssText = "display:flex;align-items:center;gap:8px;min-width:0;";

    this.headerBackBtn = document.createElement("button");
    this.headerBackBtn.innerHTML = "&#8592;";
    this.headerBackBtn.setAttribute("aria-label", "Go back to previous step");
    this.headerBackBtn.style.cssText = `
      width:30px;height:30px;border-radius:${RADIUS.sm}px;flex-shrink:0;
      display:none;align-items:center;justify-content:center;
      border:1.5px solid ${this.theme.border};cursor:pointer;
      font-size:16px;line-height:1;
      background:${this.theme.cardBg};color:${this.theme.text};
      transition:all 0.2s ease;
    `;
    this.headerBackBtn.style.cssText += `:focus-visible{outline:2px solid ${this.theme.primary};outline-offset:2px;}`;
    this.headerBackBtn.addEventListener("mouseenter", () => {
      if (this.headerBackBtn) {
        this.headerBackBtn.style.background = this.theme.shimmer;
      }
    });
    this.headerBackBtn.addEventListener("mouseleave", () => {
      if (this.headerBackBtn) {
        this.headerBackBtn.style.background = this.theme.cardBg;
      }
    });
    leftArea.appendChild(this.headerBackBtn);

    // Client's per-config logo — hidden until a valid logo_url is known and
    // actually loads (createHeader() runs before initializeConfig()
    // resolves, see the "Update brand logo" patch-in in initialize() below).
    // No placeholder/spinner: an empty gap for a fraction of a second reads
    // better than a flash of a generic mark that isn't this client's.
    this.brandLogoImg = document.createElement("img");
    this.brandLogoImg.alt = "";
    this.brandLogoImg.style.cssText = `
      height:28px;max-width:120px;object-fit:contain;flex-shrink:0;
      display:none;
    `;
    this.brandLogoImg.addEventListener("error", () => {
      // A stale/unreachable logo_url must not leave a broken-image icon in
      // the header — fall back to text-only, same as no logo being set.
      if (this.brandLogoImg) this.brandLogoImg.style.display = "none";
    });
    leftArea.appendChild(this.brandLogoImg);

    this.brandLabel = document.createElement("div");
    this.brandLabel.setAttribute("aria-label", "Verification provider");
    this.brandLabel.style.cssText = `
      font-size:15px;font-weight:700;color:${this.theme.text};
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
    `;
    this.brandLabel.textContent = this.sdkConfig?.brand_name || "SmartComply";
    leftArea.appendChild(this.brandLabel);
    header.appendChild(leftArea);

    // Step indicator: stage dots + label
    const stepIndicator = document.createElement("div");
    stepIndicator.style.cssText = `
      display:flex;align-items:center;gap:8px;flex-shrink:0;
    `;

    this.stageDots = document.createElement("div");
    this.stageDots.style.cssText = `display:flex;align-items:center;gap:4px;`;
    for (let i = 0; i < 2; i++) {
      const dot = document.createElement("span");
      dot.style.cssText = `
        width:6px;height:6px;border-radius:50%;
        background:${this.theme.border};transition:background 0.2s ease;
      `;
      this.stageDots.appendChild(dot);
    }
    stepIndicator.appendChild(this.stageDots);

    this.stepLabel = document.createElement("div");
    this.stepLabel.setAttribute("aria-live", "polite");
    this.stepLabel.setAttribute("aria-atomic", "true");
    this.stepLabel.style.cssText = `
      font-size:12px;color:${this.theme.textMuted};
      font-variant-numeric:tabular-nums;flex-shrink:0;
    `;
    stepIndicator.appendChild(this.stepLabel);
    header.appendChild(stepIndicator);

    // Close button
    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = "✕";
    closeBtn.setAttribute("aria-label", "Close identity verification");
    closeBtn.style.cssText = `
      width:32px;height:32px;border-radius:${RADIUS.sm}px;flex-shrink:0;
      display:flex;align-items:center;justify-content:center;
      border:none;cursor:pointer;font-size:16px;
      background:${this.theme.shimmer};color:${this.theme.textMuted};
      transition:all 0.2s ease;
    `;
    closeBtn.addEventListener("mouseenter", () => {
      closeBtn.style.background = this.theme.errorBg;
      closeBtn.style.color = this.theme.error;
    });
    closeBtn.addEventListener("mouseleave", () => {
      closeBtn.style.background = this.theme.shimmer;
      closeBtn.style.color = this.theme.textMuted;
    });
    closeBtn.addEventListener("click", () => this.close());
    header.appendChild(closeBtn);

    return header;
  }

  private createFooter(): HTMLElement {
    const footer = document.createElement("div");
    footer.style.cssText = `
      padding:9px 20px;border-top:1px solid ${this.theme.border};
      display:flex;align-items:center;justify-content:center;gap:6px;
    `;

    const powered = document.createElement("span");
    powered.style.cssText = `font-size:11px;color:${this.theme.textMuted};`;
    powered.textContent = "Powered by ";

    const brand = document.createElement("a");
    brand.href = "https://adhere.smartcomply.com/";
    brand.target = "_blank";
    brand.rel = "noopener noreferrer";
    brand.style.cssText = `font-size:11px;font-weight:700;color:${this.theme.primary};text-decoration:none;`;
    brand.textContent = "Adhere";
    brand.addEventListener("mouseenter", () => { brand.style.textDecoration = "underline"; });
    brand.addEventListener("mouseleave", () => { brand.style.textDecoration = "none"; });

    footer.appendChild(powered);
    footer.appendChild(brand);
    return footer;
  }

  private createStepTitle(text: string): HTMLElement {
    const el = document.createElement("h3");
    el.style.cssText = `
      color:${this.theme.text};font-size:${TYPE.heading}px;font-weight:700;
      margin:0;
    `;
    el.textContent = text;
    return el;
  }

  private createOptionCard(
    title: string,
    subtitle: string,
    onClick: () => void
  ): HTMLElement {
    const card = document.createElement("button");
    card.setAttribute("aria-label", subtitle ? `${title}: ${subtitle}` : title);
    card.style.cssText = `
      width:100%;padding:13px 16px;border-radius:${RADIUS.md}px;
      background:${this.theme.cardBg};
      border:2px solid ${this.theme.border};
      cursor:pointer;text-align:left;
      display:flex;align-items:center;justify-content:space-between;
      transition:all 0.2s ease;
    `;
    card.addEventListener("mouseenter", () => {
      card.style.borderColor = this.theme.primary;
      card.style.background = this.theme.shimmer;
      card.style.transform = "translateY(-1px)";
    });
    card.addEventListener("mouseleave", () => {
      card.style.borderColor = this.theme.border;
      card.style.background = this.theme.cardBg;
      card.style.transform = "translateY(0)";
    });
    card.addEventListener("click", onClick);

    const textWrap = document.createElement("div");
    textWrap.style.cssText = "flex:1 1 auto;min-width:0;";

    const titleEl = document.createElement("div");
    titleEl.style.cssText = `color:${this.theme.text};font-size:14px;font-weight:600;line-height:1.35;`;
    titleEl.textContent = title;
    textWrap.appendChild(titleEl);

    if (subtitle) {
      const subEl = document.createElement("div");
      subEl.style.cssText = `color:${this.theme.textMuted};font-size:12px;line-height:1.4;margin-top:2px;`;
      subEl.textContent = subtitle;
      textWrap.appendChild(subEl);
    }

    const arrow = document.createElement("span");
    arrow.style.cssText = `color:${this.theme.textMuted};font-size:18px;flex-shrink:0;margin-left:12px;line-height:1;`;
    arrow.textContent = "→";

    card.appendChild(textWrap);
    card.appendChild(arrow);
    return card;
  }

  private createPrimaryButton(text: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.textContent = text;
    btn.style.cssText = `
      width:100%;padding:14px;border-radius:${RADIUS.md}px;
      font-size:15px;font-weight:600;
      background:${this.theme.primary};color:#fff;
      border:none;cursor:pointer;
      transition:all 0.2s ease;
      box-shadow:0 2px 8px ${this.theme.primaryGlow};
    `;
    btn.addEventListener("mouseenter", () => {
      btn.style.background = this.theme.primaryHover;
      btn.style.transform = "translateY(-1px)";
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.background = this.theme.primary;
      btn.style.transform = "translateY(0)";
    });
    return btn;
  }

  private createSecondaryButton(text: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.textContent = text;
    btn.style.cssText = `
      width:100%;padding:12px;border-radius:${RADIUS.md}px;
      font-size:14px;font-weight:500;
      background:transparent;color:${this.theme.textSecondary};
      border:none;cursor:pointer;
      transition:all 0.2s ease;
    `;
    btn.addEventListener("mouseenter", () => {
      btn.style.color = this.theme.text;
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.color = this.theme.textSecondary;
    });
    return btn;
  }

  /** Click handler that fires once and disables the button, preventing a double-tap double-dispatch. */
  private _onceClick(btn: HTMLButtonElement, handler: () => void): void {
    btn.addEventListener("click", () => {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.style.opacity = "0.6";
      btn.style.cursor = "default";
      handler();
    });
  }

  private isDocumentFlow(): boolean {
    // 1. Check verification_type — handle both string and array from backend
    const vType = this.sdkConfig?.verification_type;
    if (vType) {
      const types: string[] = Array.isArray(vType) ? vType : [vType];
      if (types.includes("document_verification")) return true;
      if (types.includes("data_verification")) return false;
    }
    // 2. Fallback: inspect channel fields — any upload field = document flow.
    // channels[country] is now split {data_verification, document_verification}
    // (see ChannelsByCategory) — flatten both sub-lists per country, not the
    // per-country objects themselves.
    const channels = this.sdkConfig?.channels || {};
    const allFields = Object.values(channels)
      .flatMap((byCategory: any) => [
        ...(byCategory.data_verification || []),
        ...(byCategory.document_verification || []),
      ])
      .flatMap((ch: any) => ch.fields || []);
    return allFields.some((f: any) => isUploadFieldType(f.type));
  }

  /**
   * True when the SDK Config is set to skip data/document verification
   * entirely and only run the liveness/selfie check — no country picker,
   * no ID-type picker, no ID-number entry or document capture. Unlike
   * isDocumentFlow(), there's no channel-shape fallback to infer this from:
   * a liveness-only config carries no channels at all, so the only signal
   * is verification_type itself.
   */
  private isLivenessOnlyFlow(): boolean {
    const vType = this.sdkConfig?.verification_type;
    if (!vType) return false;
    const types: string[] = Array.isArray(vType) ? vType : [vType];
    return types.includes("liveness_only");
  }

  /**
   * The "go back one step" target for a given step, if that step is
   * navigable — shared by the header back button and the browser
   * back-button (popstate) handler in mount() so both agree on the same
   * step order.
   */
  private _getBackAction(step: StepName): (() => void) | undefined {
    const countries = Object.keys(this.sdkConfig?.channels || {});
    const backTargets: Partial<Record<StepName, () => void>> = {
      id_type:          () => this.showStep(countries.length > 1 ? "country" : "welcome"),
      country:          () => this.showStep("welcome"),
      id_input:         () => this.showStep("id_type"),
      confirm_identity: () => this.showStep("id_input"),
      document_capture: () => { this.docCapture?.destroy(); this.showStep("id_type"); },
      document_confirm: () => { this.docCapture?.destroy(); this.showStep("document_capture"); },
    };
    // liveness-only skips straight from welcome to liveness with nothing in
    // between — deliberately no backTargets["liveness"] entry added here,
    // so the back button stays hidden on that step in this mode, same as
    // it already is for every other mode (liveness has no back target for
    // document/data flows either — nothing to go back to except closing).
    return backTargets[step];
  }

  private updateProgress(): void {
    const isLivenessOnly = this.isLivenessOnlyFlow();
    const isDocFlow = isLivenessOnly ? false : this.isDocumentFlow();
    const countries = Object.keys(this.sdkConfig?.channels || {});
    const singleCountry = countries.length <= 1;

    // Two-stage action steps — Stage 1: validate identity, Stage 2: liveness.
    // Liveness-only has no Stage 1 at all — welcome goes straight to
    // liveness — so stage1Steps stays empty and the progress bar reflects
    // a genuinely single-stage flow instead of implying a step that never
    // happens in this mode.
    const stage1Steps: StepName[] = isLivenessOnly
      ? []
      : isDocFlow
        ? (["country", "id_type", "document_capture", "ocr_gate", "document_confirm"] as StepName[]).filter(s => !(s === "country" && singleCountry))
        : (["country", "id_type", "id_input", "confirm_identity"] as StepName[]).filter(s => !(s === "country" && singleCountry));

    const stage2Steps: StepName[] = ["liveness"];
    const allActionSteps: StepName[] = [...stage1Steps, ...stage2Steps];

    const totalSteps = allActionSteps.length;
    const actionIndex = allActionSteps.indexOf(this.currentStep);

    const pct =
      this.currentStep === "done" ? 100
      : actionIndex >= 0 ? ((actionIndex + 1) / totalSteps) * 100
      : 0;

    if (this.progressBar) {
      this.progressBar.style.width = `${pct}%`;
    }

    // Stage label: "Stage 1 of 2: Verify Identity" / "Stage 2 of 2: Face Check"
    // — liveness-only has no Stage 1, so it just says "Face Check" with no
    // "of 2" framing, since there's only ever the one stage in this mode.
    if (this.stepLabel) {
      if (this.currentStep === "loading" || this.currentStep === "welcome") {
        this.stepLabel.textContent = "";
      } else if (this.currentStep === "done") {
        this.stepLabel.textContent = "Complete ✓";
      } else if (stage2Steps.includes(this.currentStep)) {
        this.stepLabel.textContent = isLivenessOnly ? "Face Check" : "Stage 2 of 2: Face Check";
      } else if (stage1Steps.includes(this.currentStep)) {
        this.stepLabel.textContent = isDocFlow ? "Stage 1 of 2: Scan Document" : "Stage 1 of 2: Verify Identity";
      } else {
        this.stepLabel.textContent = "";
      }
    }

    // W3: Stage dots — dot 1 lights once Stage 1 is reached, dot 2 once Stage 2 is reached
    if (this.stageDots) {
      const dots = Array.from(this.stageDots.children) as HTMLSpanElement[];
      const inStage1 = stage1Steps.includes(this.currentStep);
      const inStage2 = stage2Steps.includes(this.currentStep);
      const isComplete = this.currentStep === "done";
      const stage1Active = inStage1 || inStage2 || isComplete;
      const stage2Active = inStage2 || isComplete;
      if (dots[0]) dots[0].style.background = stage1Active ? this.theme.primary : this.theme.border;
      if (dots[1]) dots[1].style.background = stage2Active ? this.theme.primary : this.theme.border;
    }

    // Back button — show for navigable steps only
    if (this.headerBackBtn) {
      const backAction = this._getBackAction(this.currentStep);
      if (backAction) {
        this.headerBackBtn.style.display = "flex";
        const newBtn = this.headerBackBtn.cloneNode(true) as HTMLButtonElement;
        newBtn.style.cssText = this.headerBackBtn.style.cssText;
        this.headerBackBtn.parentNode?.replaceChild(newBtn, this.headerBackBtn);
        this.headerBackBtn = newBtn;
        this.headerBackBtn.addEventListener("mouseenter", () => {
          if (this.headerBackBtn) this.headerBackBtn.style.background = this.theme.shimmer;
        });
        this.headerBackBtn.addEventListener("mouseleave", () => {
          if (this.headerBackBtn) this.headerBackBtn.style.background = this.theme.cardBg;
        });
        this.headerBackBtn.addEventListener("click", backAction);
      } else {
        this.headerBackBtn.style.display = "none";
      }
    }
  }

  private applyTheme(): void {
    if (this.overlay) {
      this.overlay.style.background = this.theme.overlay;
    }
    if (this.modal) {
      this.modal.style.background = this.theme.bg;
      this.modal.style.boxShadow = this.theme.shadow;
    }
  }

  private injectStyles(): void {
    if (document.getElementById("sc-flow-styles")) return;

    const style = document.createElement("style");
    style.id = "sc-flow-styles";
    style.textContent = `
      @keyframes sc-pop {
        0% { transform: scale(0.8); opacity: 0; }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); opacity: 1; }
      }
      #sc-flow-modal * {
        box-sizing: border-box;
      }
      #sc-flow-modal button:focus-visible,
      #sc-flow-modal select:focus-visible,
      #sc-flow-modal input:focus-visible {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }
      #sc-flow-modal::-webkit-scrollbar {
        width: 4px;
      }
      #sc-flow-content::-webkit-scrollbar {
        width: 4px;
      }
      #sc-flow-content::-webkit-scrollbar-thumb {
        background: rgba(128,128,128,0.3);
        border-radius: 4px;
      }
    `;
    document.head.appendChild(style);
  }
}
